/**
 * Карта «Барселона» для «Полёта птицы» — открытый мир на острове R = 1900.
 * Позиции — по реальным координатам (`geo(lat, lon)`): площадь Каталонии в
 * центре, до 4 км — 300 ед/км, дальше расстояние сжимается, чтобы Камп Ноу и
 * Тибидабо поместились на острове. Море — на юго-востоке, берег идёт по
 * реальной линии; на северо-западе — хребет Кольсерола с Тибидабо, у порта —
 * гора Монжуик. Эшампле — сетка восьмиугольных кварталов со скошенными углами
 * под 45°, как у Серды; выше Диагонали — Грасия и склоны, у моря — Готический
 * квартал и Барселонета. Достопримечательности: Саграда Фамилия, Каса Мила и
 * Каса Батло, парк Гуэль, площадь Каталонии и Рамбла с колонной Колумба,
 * собор, Триумфальная арка и Сьютаделья, торре Глориес, отель Arts с
 * «Рыбой» Гери, отель W, Монжуик (MNAC, Волшебный фонтан, площадь Испании,
 * крепость, башня Калатравы), канатка через порт, Камп Ноу, Тибидабо с
 * храмом, колесом и башней Кольсерола. Всё из примитивов, мир детерминирован
 * по seed. Время суток: 0 — день, 0,5 — закат, 1 — ночь с огнями.
 */
/* eslint-disable */
import * as THREE from 'three';
import type { BirdFlightWorld } from './ekibastuzWorld.ts';
import {
  distanceCuller, facadeMaterial, instancedBySector, mergeStaticMeshes,
} from './worldOptimize.ts';

export function* buildBarcelonaWorld(scene: THREE.Scene): Generator<number, BirdFlightWorld, void> {
  // ─── Случайность по seed: одинаковый мир у всех игроков ───────────────────
  function mulberry32(a) {
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      let t = Math.imul(a ^ a >>> 15, 1 | a);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }
  const rnd = mulberry32(20261015);
  const rand = (a, b) => a + rnd() * (b - a);
  const pick = (list) => list[Math.floor(rnd() * list.length)];
  const smooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

  yield 0.05;
  // ─── Небо: шейдерная сфера, цвета меняются со временем суток ─────────────
  const skyUniforms = {
    top: { value: new THREE.Color() }, mid: { value: new THREE.Color() }, horizon: { value: new THREE.Color() },
    sunDir: { value: new THREE.Vector3() }, sunColor: { value: new THREE.Color() }, sunStrength: { value: 1 },
  };
  const sky = new THREE.Mesh(new THREE.SphereGeometry(1400, 32, 16), new THREE.ShaderMaterial({
    side: THREE.BackSide, depthWrite: false, fog: false, uniforms: skyUniforms,
    vertexShader: 'varying vec3 vDir; void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
    fragmentShader: `
      uniform vec3 top, mid, horizon, sunColor, sunDir; uniform float sunStrength; varying vec3 vDir;
      void main(){
        float h = vDir.y;
        vec3 c = h > 0.18 ? mix(mid, top, smoothstep(0.18, 0.8, h)) : mix(horizon, mid, smoothstep(-0.02, 0.18, h));
        float d = max(dot(normalize(vDir), normalize(sunDir)), 0.0);
        c += sunColor * (pow(d, 2500.0) * 4.0 + pow(d, 90.0) * 0.35) * sunStrength;
        gl_FragColor = vec4(c, 1.0);
        #include <colorspace_fragment>
      }`,
  }));
  scene.add(sky);

  const nightStars = (() => {
    const pts = [];
    for (let i = 0; i < 1400; i++) {
      const u = rnd() * Math.PI * 2, v = Math.acos(rand(0.05, 1));
      pts.push(Math.sin(v) * Math.cos(u) * 1350, Math.cos(v) * 1350, Math.sin(v) * Math.sin(u) * 1350);
    }
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    const p = new THREE.Points(g, new THREE.PointsMaterial({ color: 0xffffff, size: 2.2, sizeAttenuation: false, transparent: true, fog: false }));
    scene.add(p); return p;
  })();

  // Туман только у дальней границы, как в Алматы: прячет край острова и
  // обрезку по camera.far (сфера неба 1400 м).
  const FOG_NEAR_DAY = 1150, FOG_NEAR_NIGHT = 900;
  const fog = new THREE.Fog(0xffffff, FOG_NEAR_DAY, 1380);
  scene.fog = fog;
  const hemi = new THREE.HemisphereLight(0xffffff, 0x3a3048, 1);
  scene.add(hemi);
  const sun = new THREE.DirectionalLight(0xffffff, 1);
  scene.add(sun);

  const lambert = (color, extra = {}) => new THREE.MeshLambertMaterial({ color, flatShading: true, ...extra });
  const box = new THREE.BoxGeometry(1, 1, 1);

  // Огни: днём тусклые, ночью горят.
  const lights = [];
  const redLights = [];
  function lightMat(dayColor, nightColor) {
    const m = new THREE.MeshBasicMaterial({ color: dayColor });
    lights.push({ m, day: new THREE.Color(dayColor), night: new THREE.Color(nightColor) });
    return m;
  }

  const SECTOR_SIZE = 1300;
  function instanced(geo, material, list) {
    if (list.length) instancedBySector(scene, geo, material, list, SECTOR_SIZE);
  }
  // Точка в локальной системе объекта: u — вправо, v — вперёд (по ry).
  const loc = (c, ry, u, v) => ({ x: c.x + u * Math.cos(ry) + v * Math.sin(ry), z: c.z - u * Math.sin(ry) + v * Math.cos(ry) });
  function add(geo, material, x, y, z, sx = 1, sy = 1, sz = 1, ry = 0) {
    const m = new THREE.Mesh(geo, material);
    m.position.set(x, y, z); m.scale.set(sx, sy, sz); m.rotation.y = ry; scene.add(m); return m;
  }

  yield 0.08;
  // ─── Координаты ──────────────────────────────────────────────────────────
  // (0,0) — площадь Каталонии, x < 0 — восток, z > 0 — север (как в Алматы).
  // До 4 км — честно, 300 ед/км; дальше сжатие: Камп Ноу ~1200, Тибидабо ~1470.
  const ISLAND = 1900;
  const ORIGIN = { lat: 41.3870, lon: 2.1700 };
  const KM_LAT = 111.2, KM_LON = 83.5, SCALE = 300, NEAR_KM = 4;
  function geo(lat, lon) {
    const n = (lat - ORIGIN.lat) * KM_LAT, e = (lon - ORIGIN.lon) * KM_LON, d = Math.hypot(n, e);
    const r = d <= NEAR_KM ? d * SCALE : NEAR_KM * SCALE + 600 * (1 - Math.exp(-(d - NEAR_KM) / 3));
    const k = d ? r / d : 0;
    return { x: -e * k, z: n * k };
  }
  // Оси сетки Эшампле: a — вдоль берега (на северо-восток), b — от моря к горам
  // (на северо-запад). Улицы Серды идут под 45° к сторонам света.
  const R2 = Math.SQRT1_2;
  const toAB = (x, z) => ({ a: (z - x) * R2, b: (x + z) * R2 });
  const fromAB = (a, b) => ({ x: (b - a) * R2, z: (a + b) * R2 });

  const GLORIES_PARK_LL = [41.4040, 2.1857];
  const P = {
    sagrada: geo(41.4036, 2.1744),
    milà: geo(41.3953, 2.1619),
    batllo: geo(41.3917, 2.1650),
    guell: geo(41.4145, 2.1527),
    cathedral: geo(41.3840, 2.1762),
    columbus: geo(41.3758, 2.1778),
    arc: geo(41.3911, 2.1806),
    ciutadella: geo(41.3881, 2.1873),
    glories: geo(41.4034, 2.1897),
    arts: geo(41.3868, 2.1963),
    whotel: geo(41.3685, 2.1900),
    mnac: geo(41.3685, 2.1534),
    fountain: geo(41.3712, 2.1517),
    espanya: geo(41.3750, 2.1492),
    castle: geo(41.3634, 2.1662),
    calatrava: geo(41.3646, 2.1520),
    stadium: geo(41.3647, 2.1556),
    campnou: geo(41.3809, 2.1228),
    tibidabo: geo(41.4220, 2.1190),
    collserola: geo(41.4171, 2.1142),
    stSebastia: geo(41.3713, 2.1885),
    jaume: geo(41.3720, 2.1803),
    miramar: geo(41.3700, 2.1720),
    macia: geo(41.3925, 2.1428),
    forum: geo(41.4110, 2.2210),
  };

  yield 0.10;
  // ─── Берег и рельеф ──────────────────────────────────────────────────────
  // Берег — реальная ломаная, в осях (a, b) это почти прямая: для каждого a
  // находим b берега. s — расстояние вглубь суши (< 0 — море).
  const GLORIES_PARK = geo(GLORIES_PARK_LL[0], GLORIES_PARK_LL[1]);
  const COAST = [
    [41.320, 2.120], [41.340, 2.150], [41.356, 2.168], [41.368, 2.183], [41.371, 2.190], [41.378, 2.1935],
    [41.386, 2.2005], [41.395, 2.209], [41.404, 2.218], [41.413, 2.229], [41.425, 2.243], [41.445, 2.262],
  ].map(([la, lo]) => { const g = geo(la, lo); return toAB(g.x, g.z); }).sort((p, q) => p.a - q.a);
  function coastB(a) {
    if (a <= COAST[0].a) return COAST[0].b + (a - COAST[0].a) * (COAST[1].b - COAST[0].b) / (COAST[1].a - COAST[0].a);
    for (let i = 0; i < COAST.length - 1; i++) {
      const p = COAST[i], q = COAST[i + 1];
      if (a <= q.a) return p.b + (q.b - p.b) * (a - p.a) / (q.a - p.a);
    }
    const p = COAST[COAST.length - 2], q = COAST[COAST.length - 1];
    return q.b + (a - q.a) * (q.b - p.b) / (q.a - p.a);
  }
  const coastDist = (x, z) => { const { a, b } = toAB(x, z); return b - coastB(a); };

  // Порт Вель — бухта между Рамблой и Барселонетой.
  const PORT = [[41.3762, 2.1792], [41.3800, 2.1838], [41.3760, 2.1880], [41.3712, 2.1868], [41.3690, 2.1830], [41.3705, 2.1785]]
    .map(([la, lo]) => geo(la, lo));
  function inPoly(poly, x, z) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const a = poly[i], b = poly[j];
      if ((a.z > z) !== (b.z > z) && x < (b.x - a.x) * (z - a.z) / (b.z - a.z) + a.x) inside = !inside;
    }
    return inside;
  }
  const inPort = (x, z) => inPoly(PORT, x, z);

  const TIBI = toAB(P.tibidabo.x, P.tibidabo.z);
  const gauss = (x, z, c, r) => Math.exp(-((x - c.x) ** 2 + (z - c.z) ** 2) / (2 * r * r));
  const MNAC_HILL = { x: P.mnac.x - 40, z: P.mnac.z - 40 };
  const ROVIRA = geo(41.4192, 2.1617), CREUS = geo(41.4148, 2.1515), PEDRALBES = geo(41.3960, 2.1080);
  // Высота без обрезки по уровню моря: дно моря — минус, суша — плюс.
  function rawHeight(x, z) {
    const { a, b } = toAB(x, z);
    const s = b - coastB(a);
    let h = THREE.MathUtils.lerp(-5, 1, smooth(-14, 6, s));
    if (inPort(x, z)) h = -5;
    // Город медленно поднимается к горам.
    h += smooth(400, 1100, s) * 8;
    // Кольсерола: хребет параллельно берегу, Тибидабо — вершина.
    const ridge = smooth(TIBI.b - 330, TIBI.b - 40, b);
    h += ridge * (175 + 45 * Math.sin(a * 0.006 + 0.7) + 22 * Math.sin(a * 0.019 + b * 0.011));
    h += 85 * Math.exp(-((a - TIBI.a) ** 2 + (b - TIBI.b) ** 2) / (2 * 150 * 150));
    // Монжуик — гора у порта, крутая к морю.
    h += 92 * Math.exp(-((x - P.castle.x - 10) ** 2) / (2 * 170 * 170) - ((z - P.castle.z - 25) ** 2) / (2 * 120 * 120));
    h += 38 * gauss(x, z, MNAC_HILL, 130);
    // Холмы над Грасией: Турó де ла Ровира, холм парка Гуэль; Педральбес.
    h += 70 * gauss(x, z, ROVIRA, 70) + 50 * gauss(x, z, CREUS, 60) + 20 * gauss(x, z, PEDRALBES, 120);
    // Ровные площадки под постройками на Монжуике.
    [[P.mnac, 46, 60], [P.castle, 30, 0], [P.stadium, 55, 0]].forEach(([c, r]: any) => {
      const d = Math.hypot(x - c.x, z - c.z);
      if (d < r * 1.6) h = THREE.MathUtils.lerp(levelAt(c), h, smooth(r, r * 1.6, d));
    });
    // За краем острова на суше — холмы, на море — открытая вода.
    if (s > 0) h += smooth(ISLAND * 0.85, ISLAND * 1.15, Math.hypot(x, z)) * 40;
    return h;
  }
  // Уровни площадок считаем один раз по рельефу без площадок.
  const LEVELS = new Map();
  function levelAt(c) {
    if (!LEVELS.has(c)) {
      const { a, b } = toAB(c.x, c.z);
      let h = smooth(400, 1100, b - coastB(a)) * 8 + 1;
      h += 92 * Math.exp(-((c.x - P.castle.x - 10) ** 2) / (2 * 170 * 170) - ((c.z - P.castle.z - 25) ** 2) / (2 * 120 * 120));
      h += 38 * gauss(c.x, c.z, MNAC_HILL, 130);
      LEVELS.set(c, h);
    }
    return LEVELS.get(c);
  }
  const height = (x, z) => Math.max(0, rawHeight(x, z));

  {
    const seg = 220;
    const grid = new THREE.PlaneGeometry(ISLAND * 2.6, ISLAND * 2.6, seg, seg);
    grid.rotateX(-Math.PI / 2);
    const gridPos = grid.attributes.position;
    for (let i = 0; i < gridPos.count; i++) gridPos.setY(i, rawHeight(gridPos.getX(i), gridPos.getZ(i)));
    const geom = grid.toNonIndexed();
    grid.dispose();
    const pos = geom.attributes.position;
    const city = [0xb7ad9c, 0xaea595, 0xbcb3a3].map((c) => new THREE.Color(c));
    const sand = [0xead9aa, 0xe4d19d].map((c) => new THREE.Color(c));
    const scrub = [0x7d9550, 0x6f8a46, 0x86a05a].map((c) => new THREE.Color(c));
    const forest = [0x4f7438, 0x47693a, 0x587d40].map((c) => new THREE.Color(c));
    const rock = [0x9c8f78, 0x8f836d].map((c) => new THREE.Color(c));
    const seabed = new THREE.Color(0x2f6f8f);
    const colors = [];
    for (let f = 0; f < pos.count; f += 3) {
      const x = (pos.getX(f) + pos.getX(f + 1) + pos.getX(f + 2)) / 3, z = (pos.getZ(f) + pos.getZ(f + 1) + pos.getZ(f + 2)) / 3;
      const y = (pos.getY(f) + pos.getY(f + 1) + pos.getY(f + 2)) / 3;
      const s = coastDist(x, z);
      const c = y < 0 ? seabed : s < 22 && y < 4 ? pick(sand) : y > 330 ? pick(rock) : y > 70 ? pick(forest) : y > 20 ? pick(scrub) : pick(city);
      for (let k = 0; k < 3; k++) colors.push(c.r, c.g, c.b);
    }
    geom.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geom.computeVertexNormals();
    scene.add(new THREE.Mesh(geom, new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true })));
  }
  // Море: большая плоскость до горизонта, бирюзовое у берега не делаем —
  // мелководье дают цвет дна и прозрачность не нужна.
  const sea = add(new THREE.PlaneGeometry(ISLAND * 3.2, ISLAND * 3.2), new THREE.MeshPhongMaterial({ color: 0x2f86b8, specular: 0x4a6a80, shininess: 25 }), 0, 0.4, 0);
  sea.rotation.x = -Math.PI / 2;

  yield 0.16;
  // ─── Общие строители ─────────────────────────────────────────────────────
  // Участки достопримечательностей: кварталы и деревья сюда не ставятся.
  // [x, z, радиус].
  const LOTS = [
    [P.sagrada, 42], [P.batllo, 14], [P.guell, 75], [P.cathedral, 22], [P.columbus, 30],
    [P.arc, 22], [P.ciutadella, 85], [P.glories, 22], [GLORIES_PARK, 44], [P.arts, 45], [P.whotel, 30], [P.mnac, 60],
    [P.fountain, 40], [P.espanya, 45], [P.castle, 45], [P.calatrava, 25], [P.stadium, 55], [P.campnou, 100],
    [P.tibidabo, 55], [P.collserola, 25], [P.stSebastia, 12], [P.jaume, 12], [P.miramar, 20], [{ x: 0, z: 0 }, 34],
  ].map(([c, r]: any) => ({ x: c.x, z: c.z, r }));
  const inLot = (x, z, m = 0) => LOTS.some((l) => Math.hypot(x - l.x, z - l.z) < l.r + m);
  // Монжуик — парк: жилых домов на горе нет.
  const onMontjuic = (x, z) => ((x - (P.castle.x + P.mnac.x) / 2) / 330) ** 2 + ((z - (P.castle.z + P.mnac.z) / 2 + 20) / 200) ** 2 < 1;

  const trunks = [], crowns = [], palmTrunks = [], palmLeaves = [], lampPoles = [], lampHeads = [];
  // Пятна построек (x, z, радиус, двор): деревья, попавшие внутрь, в конце убираются.
  const FOOTPRINTS = [];
  const facadeGlow = { value: 0 };
  const facade = (color) => facadeMaterial(color, facadeGlow);
  const stoneMats = [facade(0xe3d6bd), facade(0xd6c4a2), facade(0xeadfc8)];
  const glassMats = [facade(0x5d8fb8), facade(0x6f9ec2), facade(0x4f7ea8)];
  const bodies = new Map();
  const terracotta = lambert(0xb5603a);
  function addBody(mat, it) { if (!bodies.has(mat)) bodies.set(mat, []); bodies.get(mat).push(it); }
  // Дом-коробка: корпус в шейдере фасада, крыша плоская или черепичная.
  function house(x, z, w, d, h, mat, ry = 0, tile = false, y0 = height(x, z)) {
    // Ни на улицу, ни в воду: проверяем по описанной окружности дома.
    if (onMainRoad(x, z, Math.hypot(w, d) / 2 + 1) || rawHeight(x, z) < 0.6) return;
    addBody(mat, { x, y: y0 + h / 2, z, sx: w, sy: h, sz: d, ry });
    FOOTPRINTS.push({ x, z, r: Math.hypot(w, d) / 2 * 0.85, hole: 0 });
    // Плоская крыша — верх корпуса, отдельной плиты не нужно (минус треугольники).
    if (tile) addBody(terracotta, { x, y: y0 + h + 0.3, z, sx: w + 0.4, sy: 0.6, sz: d + 0.4, ry });
  }
  function tree(x, z) {
    if (inLot(x, z) || rawHeight(x, z) < 0.6) return;
    const y = height(x, z), h = rand(6, 9);
    trunks.push({ x, y: y + h * 0.35, z, sx: 0.45, sy: h * 0.7, sz: 0.45 });
    crowns.push({ x, y: y + h * 0.8, z, sx: rand(2.4, 3.4), sy: h * 0.42, sz: rand(2.4, 3.4), ry: rnd() * 3 });
  }
  function pine(x, z) {
    const y = height(x, z), h = rand(7, 12);
    trunks.push({ x, y: y + h * 0.4, z, sx: 0.5, sy: h * 0.8, sz: 0.5 });
    // Средиземноморская пиния — плоский зонтик.
    crowns.push({ x, y: y + h * 0.85, z, sx: rand(3.5, 5), sy: h * 0.25, sz: rand(3.5, 5), ry: rnd() * 3 });
  }
  // Пальма: тонкий ствол с наклоном и шесть листьев веером.
  function palm(x, z) {
    const y = height(x, z), h = rand(9, 13), lean = rand(-0.12, 0.12);
    palmTrunks.push({ x, y: y + h / 2, z, sx: 0.5, sy: h, sz: 0.5, rz: lean });
    const tx = x - Math.sin(lean) * h / 2, ty = y + h;
    for (let k = 0; k < 6; k++) {
      const a = k / 6 * Math.PI * 2 + rnd();
      const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(0.55, a, 0, 'YXZ'));
      palmLeaves.push({ x: tx + Math.sin(a) * 2.2, y: ty - 0.6, z: z + Math.cos(a) * 2.2, sx: 1.1, sy: 0.15, sz: 5, q });
    }
  }
  const roadMat = lambert(0x55575c);
  function road(x1, z1, x2, z2, w, lamps = 0, trees = 0) {
    const dx = x2 - x1, dz = z2 - z1, len = Math.hypot(dx, dz), ry = Math.atan2(dx, dz);
    add(box, roadMat, (x1 + x2) / 2, 1.25, (z1 + z2) / 2, w, 0.3, len, ry);
    const nx = Math.cos(ry), nz = -Math.sin(ry);
    if (lamps) for (let s = 0; s <= len; s += lamps) {
      const x = x1 + dx * s / len + nx * (w / 2 + 1), z = z1 + dz * s / len + nz * (w / 2 + 1), y = height(x, z);
      lampPoles.push({ x, y: y + 4, z, sx: 0.25, sy: 8, sz: 0.25 });
      lampHeads.push({ x, y: y + 8.2, z, sx: 0.9, sy: 0.5, sz: 0.9 });
    }
    if (trees) for (let s = 0; s <= len; s += trees) [-1, 1].forEach((sd) => tree(x1 + dx * s / len + nx * sd * (w / 2 + 2), z1 + dz * s / len + nz * sd * (w / 2 + 2)));
  }

  yield 0.20;
  // ─── Эшампле: восьмиугольные кварталы Серды ──────────────────────────────
  // Квартал 113 м + улица 20 м = шаг 40 ед; углы скошены, в середине — двор.
  // Одна геометрия «кольца» на все кварталы — инстансы по материалу.
  const PITCH = 40, BLOCK = 33.5, CHAMFER = 6, COURT = 17;
  const octagon = (half, ch) => {
    const sh = [[half - ch, half], [-half + ch, half], [-half, half - ch], [-half, -half + ch], [-half + ch, -half], [half - ch, -half], [half, -half + ch], [half, half - ch]];
    return sh;
  };
  const ringShape = new THREE.Shape(octagon(BLOCK / 2, CHAMFER).map(([u, v]) => new THREE.Vector2(u, v)));
  ringShape.holes.push(new THREE.Path(octagon(COURT / 2, CHAMFER * 0.5).reverse().map(([u, v]) => new THREE.Vector2(u, v))));
  const ringGeo = new THREE.ExtrudeGeometry(ringShape, { depth: 1, bevelEnabled: false });
  ringGeo.rotateX(-Math.PI / 2); ringGeo.translate(0, -0.5, 0);
  // Полный квартал без двора — для пары кварталов, застроенных целиком.
  const ringCourt = new THREE.ExtrudeGeometry(new THREE.Shape(octagon(COURT / 2 + 0.5, CHAMFER * 0.5).map(([u, v]) => new THREE.Vector2(u, v))), { depth: 1, bevelEnabled: false });
  ringCourt.rotateX(-Math.PI / 2); ringCourt.translate(0, -0.5, 0);
  const blockMats = [facade(0xe6d8bd), facade(0xdcc8a6), facade(0xeee4d2)];
  const blocks = new Map(), courts = [];
  // Квартал, на углу которого стоит Каса Мила (её строим поверх угла).
  const MILA: { block: { a: number; b: number; h0: number } | null } = { block: null };
  // Диагональ — прямой проспект от площади Франсеска Масиа до Глориес и Форума.
  const DIAG_A = toAB(P.macia.x, P.macia.z), DIAG_B = toAB(P.glories.x, P.glories.z);
  const diagB = (a) => DIAG_A.b + (DIAG_B.b - DIAG_A.b) * (a - DIAG_A.a) / (DIAG_B.a - DIAG_A.a);
  // Верхняя граница сетки: западнее Пассеч де Грасия — Диагональ (выше —
  // Сант-Жерваси), восточнее сетка заходит за Диагональ до больницы Сант-Пау
  // (Саграда Фамилия — выше Диагонали).
  const MILA_AB = toAB(P.milà.x, P.milà.z), SANTPAU_B = (() => { const g = geo(41.4115, 2.1745); return toAB(g.x, g.z).b; })();
  // Главные улицы — до застройки: дома и кварталы на полотно не ставятся.
  // Пассеч де Грасия и Гран Виа лежат в промежутке сетки, поэтому узкие (6),
  // Гран Виа — на ближайшей линии сетки; Диагональ режет кварталы, как в жизни.
  const GLORIES_A = toAB(P.glories.x, P.glories.z).a;
  const GV_B = Math.round(toAB(P.espanya.x, P.espanya.z).b / PITCH) * PITCH;
  const MAIN_ROADS = [
    { a: { x: 0, z: 0 }, b: P.columbus, w: 9, lamps: 0, trees: 6 },
    { a: { x: 0, z: 0 }, b: fromAB(0, diagB(0)), w: 6, lamps: 20, trees: 0 },
    { a: fromAB(DIAG_A.a - 400, diagB(DIAG_A.a - 400)), b: fromAB(toAB(P.forum.x, P.forum.z).a, diagB(toAB(P.forum.x, P.forum.z).a)), w: 14, lamps: 24, trees: 10 },
    { a: fromAB(toAB(P.espanya.x, P.espanya.z).a - 300, GV_B), b: fromAB(GLORIES_A + 900, GV_B), w: 6, lamps: 24, trees: 0 },
  ];
  function onMainRoad(x, z, m = 0) {
    return MAIN_ROADS.some(({ a, b, w }) => {
      const dx = b.x - a.x, dz = b.z - a.z, t = Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / (dx * dx + dz * dz)));
      return Math.hypot(x - a.x - dx * t, z - a.z - dz * t) < w / 2 + m;
    });
  }
  const gridTopB = (a) => THREE.MathUtils.lerp(diagB(a), Math.max(diagB(a), SANTPAU_B), smooth(MILA_AB.a, MILA_AB.a + 200, a));
  // Старый город — между площадью Каталонии, Паралелем, портом и Сьютадельей.
  const OLD = [[41.3862, 2.1640], [41.3870, 2.1700], [41.3911, 2.1806], [41.3850, 2.1850], [41.3800, 2.1842], [41.3762, 2.1792], [41.3745, 2.1690]]
    .map(([la, lo]) => geo(la, lo));
  const BARCELONETA = [[41.3802, 2.1860], [41.3795, 2.1925], [41.3700, 2.1898], [41.3712, 2.1868]].map(([la, lo]) => geo(la, lo));
  const ESPANYA_A = toAB(P.espanya.x, P.espanya.z).a;
  {
    const range = 1500;
    for (let i = -Math.ceil(range / PITCH); i <= range / PITCH; i++) {
      for (let j = -Math.ceil(range / PITCH); j <= range / PITCH; j++) {
        const a = (i + 0.5) * PITCH, b = (j + 0.5) * PITCH;
        const { x, z } = fromAB(a, b);
        if (Math.hypot(x, z) > ISLAND * 0.8) continue;
        const s = b - coastB(a);
        if (s < 40) continue;
        if (inLot(x, z, 14) || inPoly(OLD, x, z) || inPoly(BARCELONETA, x, z) || inPort(x, z) || onMainRoad(x, z, BLOCK / 2 - 1)) continue;
        const h0 = rawHeight(x, z);
        const below = b < gridTopB(a) - 10;
        // Эшампле и Побленоу — ниже Диагонали, восточнее площади Испании.
        if (below && a > ESPANYA_A - 20 && h0 < 20) {
          const poblenou = a > GLORIES_A + 40;
          const isMila = Math.hypot(x - P.milà.x, z - P.milà.z) < PITCH * 0.72 && !MILA.block;
          const hh = isMila ? 18 : poblenou ? rand(8, 14) : rand(15, 21);
          if (isMila) MILA.block = { a, b, h0 };
          const mat = poblenou ? pick(stoneMats) : pick(blockMats);
          if (!blocks.has(mat)) blocks.set(mat, []);
          blocks.get(mat).push({ x, y: h0 + hh / 2, z, sx: 1, sy: hh, sz: 1, ry: Math.PI / 4 });
          FOOTPRINTS.push({ x, z, r: BLOCK / 2 + 0.5, hole: COURT / 2 - 1 });
          // Двор: деревья или низкая застройка.
          if (rnd() < 0.35) courts.push({ x, y: h0 + 2, z, sx: 1, sy: 4, sz: 1, ry: Math.PI / 4 });
          else if (rnd() < 0.5) tree(x + rand(-3, 3), z + rand(-3, 3));
          // 22@: стеклянные башни в Побленоу.
          if (poblenou && rnd() < 0.12) house(x + rand(-5, 5), z + rand(-5, 5), rand(14, 20), rand(12, 18), rand(30, 55), pick(glassMats), Math.PI / 4, false, h0);
          // Платаны по улицам — по углам квартала.
          if (rnd() < 0.55) [[0, 1], [1, 0], [0, -1], [-1, 0]].forEach(([u, v]) => { if (rnd() < 0.5) { const p = fromAB(a + u * PITCH / 2, b + v * PITCH / 2); tree(p.x + rand(-3, 3), p.z + rand(-3, 3)); } });
          continue;
        }
        // Выше Диагонали и на западе — Грасия, Сант-Жерваси, Сантс: дома
        // разной высоты с черепичными крышами, к горам реже.
        if (h0 > 120 || onMontjuic(x, z)) continue;
        if (h0 > 40 && rnd() < 0.55) continue;
        if (rnd() < 0.08) continue;
        if (Math.hypot(x, z) > 1000 && rnd() < 0.4) continue; // окраины реже
        const n = h0 > 30 ? 1 : 2 + Math.floor(rnd() * 2);
        for (let k = 0; k < n; k++) {
          const p = fromAB(a + rand(-12, 12), b + rand(-12, 12));
          if (inLot(p.x, p.z, 6) || rawHeight(p.x, p.z) < 1) continue;
          const tall = h0 < 25 && rnd() < 0.15;
          house(p.x, p.z, rand(9, 16), rand(9, 14), tall ? rand(22, 34) : rand(8, 15), pick(stoneMats), Math.PI / 4 + rand(-0.2, 0.2), !tall && rnd() < 0.6);
        }
        if (rnd() < 0.6) tree(x + rand(-14, 14), z + rand(-14, 14));
      }
    }
  }

  yield 0.30;
  // ─── Готический квартал и Раваль: тесные дома с черепицей ────────────────
  {
    const xs = OLD.map((p) => p.x), zs = OLD.map((p) => p.z);
    for (let k = 0; k < 1300; k++) {
      const x = rand(Math.min(...xs), Math.max(...xs)), z = rand(Math.min(...zs), Math.max(...zs));
      if (!inPoly(OLD, x, z) || inLot(x, z, 4) || ramblaDist(x, z) < 9) continue;
      house(x, z, rand(6, 11), rand(6, 11), rand(9, 16), pick(stoneMats), rand(0, Math.PI), rnd() < 0.75);
    }
    // Барселонета: узкие длинные кварталы поперёк полуострова.
    const bx = BARCELONETA.map((p) => p.x), bz = BARCELONETA.map((p) => p.z);
    for (let k = 0; k < 420; k++) {
      const x = rand(Math.min(...bx), Math.max(...bx)), z = rand(Math.min(...bz), Math.max(...bz));
      if (!inPoly(BARCELONETA, x, z) || inLot(x, z, 4) || coastDist(x, z) < 22) continue;
      house(x, z, 5, 14, rand(9, 14), pick(stoneMats), Math.PI / 4 + 0.15, rnd() < 0.5);
    }
  }
  // Рамбла: прямая от площади Каталонии до колонны Колумба.
  function ramblaDist(x, z) {
    const dx = P.columbus.x, dz = P.columbus.z, t = Math.max(0, Math.min(1, (x * dx + z * dz) / (dx * dx + dz * dz)));
    return Math.hypot(x - dx * t, z - dz * t);
  }

  yield 0.34;
  // ─── Главные улицы: Рамбла, Пассеч де Грасия, Диагональ, Гран Виа ──────
  {
    // Рамбла, Пассеч де Грасия, Диагональ, Гран Виа — см. MAIN_ROADS.
    MAIN_ROADS.forEach(({ a, b, w, lamps, trees }) => road(a.x, a.z, b.x, b.z, w, lamps, trees));
    // Набережная вдоль пляжей с пальмами.
    for (let a = toAB(P.whotel.x, P.whotel.z).a + 60; a < toAB(P.forum.x, P.forum.z).a; a += 14) {
      const p = fromAB(a, coastB(a) + 26); palm(p.x, p.z);
    }
  }

  yield 0.38;
  // ─── Саграда Фамилия ─────────────────────────────────────────────────────
  // Песочный камень, неф крестом, 12 тонких башен-«веретён» по трём фасадам
  // с цветными навершиями, четыре башни евангелистов и центральная башня
  // Иисуса с крестом. Рядом — жёлтые башенные краны (храм всё ещё строят).
  {
    const { x, z } = P.sagrada, y0 = height(x, z);
    const ry = Math.PI / 4;   // храм стоит по сетке квартала
    const stone = lambert(0xd6c095), stoneDark = lambert(0xb59c70), stoneNew = lambert(0xe6dccb);
    const local = (u, v) => ({ x: x + u * Math.cos(ry) + v * Math.sin(ry), z: z - u * Math.sin(ry) + v * Math.cos(ry) });
    const at = (u, v, yy, geom, mat, sx = 1, sy = 1, sz = 1) => { const p = local(u, v); return add(geom, mat, p.x, y0 + yy, p.z, sx, sy, sz, ry); };
    // Неф и трансепт.
    at(0, 0, 18, box, stone, 22, 36, 60);
    at(0, 4, 18, box, stone, 52, 36, 18);
    at(0, 0, 37, box, stoneDark, 18, 2, 56);
    at(0, 4, 37, box, stoneDark, 48, 2, 14);
    at(0, -28, 17, new THREE.CylinderGeometry(10, 11, 34, 12), stone);   // апсида
    at(0, -28, 38, new THREE.ConeGeometry(10, 10, 12), stoneDark);
    // Башня-веретено: ствол, решётчатый конус, навершие.
    const bands = [];
    const spindle = (u, v, h, r, top) => {
      at(u, v, h * 0.32, new THREE.CylinderGeometry(r * 0.85, r, h * 0.64, 10), stone);
      at(u, v, h * 0.64 + h * 0.15, new THREE.CylinderGeometry(r * 0.35, r * 0.85, h * 0.3, 10), stoneDark);
      // Тёмные прорези-«жалюзи» по стволу — без них башни сливаются.
      const p = local(u, v);
      for (let yy = 8; yy < h * 0.6; yy += 5) bands.push({ x: p.x, y: y0 + yy, z: p.z, sx: r * 1.02 * (1 - 0.15 * yy / (h * 0.64)), sy: 1.2, sz: r * 1.02 * (1 - 0.15 * yy / (h * 0.64)) });
      at(u, v, h * 0.94 + 2, new THREE.SphereGeometry(r * 0.55, 8, 6), top);
      at(u, v, h * 0.94 + 4.2, new THREE.ConeGeometry(r * 0.3, 3, 6), top);
    };
    const tops = [lambert(0xd8452f), lambert(0xe9c34a), lambert(0x3f8f5f), lambert(0xf2f0e6)];
    // Фасады: Рождества (восток), Страстей (запад), Славы (юг — вход).
    [[-30, 6], [30, 6]].forEach(([u, v]) => [-9, -3, 3, 9].forEach((o, k) => spindle(u, v + o, k === 1 || k === 2 ? 86 : 76, 3.2, tops[k])));
    [-12, -4, 4, 12].forEach((o, k) => spindle(o, 32, k === 1 || k === 2 ? 92 : 82, 3.4, tops[(k + 2) % 4]));
    instanced(new THREE.CylinderGeometry(1, 1, 1, 10), lambert(0x6a5a44), bands);
    // Центральные башни: евангелисты, Мария со звездой, Иисус с крестом.
    [[-10, -8], [10, -8], [-10, 12], [10, 12]].forEach(([u, v]) => {
      at(u, v, 50, new THREE.CylinderGeometry(3.4, 4.8, 100, 12), stoneNew);
      at(u, v, 106, new THREE.ConeGeometry(3.6, 14, 12), stoneNew);
    });
    at(0, -14, 54, new THREE.CylinderGeometry(3.8, 5.2, 108, 12), stoneNew);
    at(0, -14, 114, new THREE.ConeGeometry(4, 13, 12), stoneNew);
    at(0, -14, 122, new THREE.IcosahedronGeometry(2.4, 0), lightMat(0xe8e2c8, 0xffffff));
    at(0, 2, 58, new THREE.CylinderGeometry(4.5, 7.5, 116, 16), stoneNew);
    at(0, 2, 124, new THREE.ConeGeometry(4.8, 16, 16), stoneNew);
    const crossMat = lightMat(0xf2f2f2, 0xffffff);
    at(0, 2, 138, box, crossMat, 1.4, 12, 1.4);
    at(0, 2, 139, box, crossMat, 7, 1.4, 1.4);
    at(0, 2, 139, box, crossMat, 1.4, 1.4, 7);
    // Краны.
    const crane = lambert(0xf2c230);
    [[24, -26, 110, 0.9], [-26, -20, 96, 2.2]].forEach(([u, v, h, a]) => {
      const p = local(u, v);
      add(box, crane, p.x, y0 + h / 2, p.z, 1.6, h, 1.6);
      add(box, crane, p.x + Math.sin(a) * 14, y0 + h, p.z + Math.cos(a) * 14, 1.2, 1.2, 44, a);
      add(box, lambert(0x666666), p.x - Math.sin(a) * 6, y0 + h - 1.5, p.z - Math.cos(a) * 6, 3, 3, 4, a);
    });
    // Пруды по бокам — площади Гауди и Саграда Фамилия.
    [[-1, 1], [1, -1]].forEach(([u, v]) => { const p = local(u * 52, v * 6); add(new THREE.CylinderGeometry(14, 14, 0.4, 20), new THREE.MeshPhongMaterial({ color: 0x4f9fbf, shininess: 80 }), p.x, y0 + 1.4, p.z); });
  }

  yield 0.43;
  // ─── Каса Мила и Каса Батло на Пассеч де Грасия ──────────────────────────
  {
    // Ла Педрера, как на карте: угол обычного квартала Эшампле на перекрёстке
    // Пассеч де Грасия и Провенсы. Волнистый светлый фасад огибает срезанный угол,
    // высота — как у соседей; кованые балконы — тёмные волнистые пояса; на крыше
    // «воины»-трубы. Строится в осях сетки (a, b) поверх угла квартала.
    if (MILA.block) {
      const { a: ca, b: cb, h0 } = MILA.block, mab = toAB(P.milà.x, P.milà.z);
      const su = Math.sign(mab.a - ca) || 1, sv = Math.sign(mab.b - cb) || 1;
      const S = 17, half = BLOCK / 2, ch = CHAMFER;
      // Пятно в осях квартала: квадрат у угла со срезом, как у кварталов.
      const pts = [[half - S, half - S], [half, half - S], [half, half - ch], [half - ch, half], [half - S, half]];
      const toWorld = (u, v, y) => { const w = fromAB(ca + su * u, cb + sv * v); return [w.x, h0 + y, w.z]; };
      // Призма по контуру с волной по наружным граням (u или v у края квартала).
      const prism = (y1, y2, grow, amp) => {
        const pos = [], n = pts.length, steps = 10;
        const ring = [];
        for (let i = 0; i < n; i++) {
          const [u1, v1] = pts[i], [u2, v2] = pts[(i + 1) % n];
          for (let k = 0; k < steps; k++) { const t = k / steps; ring.push([u1 + (u2 - u1) * t, v1 + (v2 - v1) * t]); }
        }
        const out = (u, v, y) => {
          const outer = u > half - 0.5 || v > half - 0.5 || u + v > 2 * half - ch - 0.5;
          if (!outer) return [u, v];
          const wv = amp * Math.sin((u + v) * 0.9 + y * 0.8) + grow;
          const nu = u + v > 2 * half - ch - 0.5 ? Math.SQRT1_2 : u > half - 0.5 ? 1 : 0;
          const nv = u + v > 2 * half - ch - 0.5 ? Math.SQRT1_2 : v > half - 0.5 ? 1 : 0;
          return [u + nu * wv, v + nv * wv];
        };
        const layers = 6;
        for (let l = 0; l < layers; l++) {
          const ya = y1 + (y2 - y1) * l / layers, yb = y1 + (y2 - y1) * (l + 1) / layers;
          for (let i = 0; i < ring.length; i++) {
            const p = ring[i], q = ring[(i + 1) % ring.length];
            const [au, av] = out(p[0], p[1], ya), [bu, bv] = out(q[0], q[1], ya), [cu, cv] = out(q[0], q[1], yb), [du, dv] = out(p[0], p[1], yb);
            [[au, av, ya], [bu, bv, ya], [cu, cv, yb], [au, av, ya], [cu, cv, yb], [du, dv, yb]].forEach(([u, v, y]) => pos.push(...toWorld(u, v, y)));
          }
        }
        // Крыша: веер из центра пятна.
        const cu0 = half - S / 2, cv0 = half - S / 2;
        for (let i = 0; i < ring.length; i++) {
          const p = ring[i], q = ring[(i + 1) % ring.length];
          const [au, av] = out(p[0], p[1], y2), [bu, bv] = out(q[0], q[1], y2);
          [[cu0, cv0], [bu, bv], [au, av]].forEach(([u, v]) => pos.push(...toWorld(u, v, y2)));
        }
        const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.computeVertexNormals();
        return g;
      };
      const stone = new THREE.MeshLambertMaterial({ color: 0xece2cf, flatShading: true, side: THREE.DoubleSide });
      add(prism(0, 19, 0.3, 0.55), stone, 0, 0, 0);
      const iron = new THREE.MeshLambertMaterial({ color: 0x4f4a42, flatShading: true, side: THREE.DoubleSide });
      [4.2, 7.6, 11, 14.4].forEach((y) => add(prism(y, y + 0.7, 0.75, 0.55), iron, 0, 0, 0));
      // «Воины» на крыше.
      for (let k = 0; k < 7; k++) {
        const [wx, wy, wz] = toWorld(half - S + 3 + (k % 4) * 3.4, half - S + 3 + Math.floor(k / 4) * 6 + (k % 2) * 2, 19);
        add(new THREE.CylinderGeometry(0.45, 0.95, 3.4, 6), lambert(0xd8cbb5), wx, wy + 1.7, wz);
        add(new THREE.SphereGeometry(0.85, 6, 4), lambert(0x8a7a64), wx, wy + 3.8, wz);
      }
    }
    // Каса Батло: сине-зелёный фасад в «чешуе», крыша-дракон, крестик-башенка.
    const b = P.batllo, by = height(b.x, b.z);
    const cv = document.createElement('canvas'); cv.width = cv.height = 128;
    const g = cv.getContext('2d'), cols = ['#5fb8c8', '#3f8fb0', '#7fc8a0', '#e9e2cf', '#4f9fd0', '#9fd0b8'];
    for (let i = 0; i < 16; i++) for (let j = 0; j < 16; j++) { g.fillStyle = cols[(i * 7 + j * 3) % cols.length]; g.beginPath(); g.arc(i * 8 + 4, j * 8 + 4, 5, 0, Math.PI * 2); g.fill(); }
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
    add(box, new THREE.MeshLambertMaterial({ map: tex }), b.x, by + 9, b.z, 10, 18, 9, Math.PI / 4);
    const dragon = add(new THREE.CylinderGeometry(4.2, 4.2, 10.4, 10, 1, false, 0, Math.PI), new THREE.MeshLambertMaterial({ color: 0x3f9a7a, flatShading: true }), b.x, by + 18, b.z);
    dragon.rotation.set(0, Math.PI / 4, Math.PI / 2); dragon.scale.set(1, 1, 0.6);
    const scales = [];
    for (let k = -4; k <= 4; k += 1.3) scales.push({ x: b.x + k * 0.7, y: by + 20.6, z: b.z + k * 0.7, sx: 1, sy: 1, sz: 1, ry: Math.PI / 4 });
    instanced(new THREE.IcosahedronGeometry(0.8, 0), lambert(0xc8452f), scales);
    add(new THREE.CylinderGeometry(0.7, 0.7, 5, 8), lambert(0xe9e2cf), b.x - 4, by + 20, b.z - 1);
    add(new THREE.SphereGeometry(1, 8, 6), lambert(0xd9a93a), b.x - 4, by + 23, b.z - 1);
  }

  yield 0.47;
  // ─── Парк Гуэль ──────────────────────────────────────────────────────────
  // Как в жизни: вытянутая терраса (площадь Природы) со змеевидной мозаичной
  // скамьёй по краю; под передней половиной — Гипостильный зал (колонны);
  // от зала вниз — парадная лестница с мозаичным драконом посередине; внизу у
  // входа — два пряничных домика с белыми «сахарными» крышами, у одного башня
  // с крестом. Всё развёрнуто к городу (вниз по склону).
  {
    const g0 = P.guell, y0 = height(g0.x, g0.z), ry = Math.atan2(-g0.x, -g0.z);   // v — к городу
    const at = (u, v) => loc(g0, ry, u, v);
    // Мозаика «тренкадис».
    const cv = document.createElement('canvas'); cv.width = cv.height = 128;
    const g = cv.getContext('2d'), tc = ['#f2f0e6', '#3a78c8', '#e9c34a', '#3fae6a', '#d8452f', '#7fc8e0'];
    g.fillStyle = '#f2f0e6'; g.fillRect(0, 0, 128, 128);
    for (let i = 0; i < 260; i++) { g.fillStyle = pick(tc); g.fillRect(rand(0, 128), rand(0, 128), rand(5, 14), rand(5, 14)); }
    const tex = new THREE.CanvasTexture(cv); tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.repeat.set(30, 1); tex.colorSpace = THREE.SRGBColorSpace;
    const tile = new THREE.MeshLambertMaterial({ map: tex });
    const tileSmall = new THREE.MeshLambertMaterial({ map: (() => { const t = tex.clone(); t.repeat.set(2, 2); t.needsUpdate = true; return t; })() });
    const sand = lambert(0xe6d6b0), white = lambert(0xf0ebe0), stone = lambert(0xb8865a), cream = lambert(0xf4efe4);
    // Терраса: эллипс 44 × 24 на высоте 7 над склоном, толщина 1,4.
    const TY = y0 + 7, TU = 22, TV = 12;
    const terrace = add(new THREE.CylinderGeometry(1, 1, 1.4, 40), sand, g0.x, TY, g0.z, TU, 1, TV, ry);
    terrace.rotation.y = ry;
    // Змеевидная скамья: труба по волнистому контуру эллипса.
    const bench = [];
    for (let i = 0; i <= 120; i++) {
      const t = i / 120 * Math.PI * 2, wv = 1 + 0.07 * Math.sin(t * 13);
      const p = at(Math.cos(t) * TU * 0.97 * wv, Math.sin(t) * TV * 0.97 * wv);
      bench.push(new THREE.Vector3(p.x, TY + 1.4, p.z));
    }
    add(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(bench, true), 240, 0.9, 6, true), tile, 0, 0, 0);
    // Гипостильный зал: колонны под передней (городской) половиной.
    const cols = [];
    for (let u = -12; u <= 12; u += 3) for (let v = 2; v <= 10; v += 2.7) {
      if ((u / TU) ** 2 + (v / TV) ** 2 > 0.85) continue;
      const p = at(u, v), base = height(p.x, p.z);
      cols.push({ x: p.x, y: (base + TY) / 2, z: p.z, sx: 1, sy: Math.max(1, TY - base), sz: 1 });
    }
    instanced(new THREE.CylinderGeometry(0.75, 0.95, 1, 10), white, cols);
    // Лестница вниз от зала к входу, двумя маршами, дракон посередине.
    for (let k = 0; k < 9; k++) {
      [-1, 1].forEach((sd) => { const p = at(sd * 4, TV + 2 + k * 3), y = height(p.x, p.z); add(box, cream, p.x, y + 0.5, p.z, 5, 1, 3, ry); });
    }
    const dp = at(0, TV + 14), dy = height(dp.x, dp.z);
    add(new THREE.CylinderGeometry(3, 3.2, 1, 16), cream, dp.x, dy + 0.5, dp.z);
    const dragon = add(new THREE.SphereGeometry(1, 12, 8), tileSmall, dp.x, dy + 1.8, dp.z, 1.3, 0.9, 3.4, ry);
    dragon.rotation.set(0.35, ry, 0, 'YXZ');
    const head = at(0, TV + 17); add(new THREE.SphereGeometry(0.9, 10, 8), tileSmall, head.x, dy + 2.4, head.z);
    // Пряничные домики у входа и ворота между ними.
    const PV = TV + 32;
    [-1, 1].forEach((sd) => {
      const p = at(sd * 11, PV), hy = height(p.x, p.z);
      add(box, stone, p.x, hy + 4, p.z, 7.5, 8, 7.5, ry);
      add(box, cream, p.x, hy + 7.6, p.z, 8, 0.8, 8, ry);
      add(new THREE.ConeGeometry(5.6, 5.4, 8), white, p.x, hy + 10.7, p.z);
      add(new THREE.CylinderGeometry(0.5, 0.9, 2, 8), cream, p.x, hy + 14, p.z);
      add(new THREE.SphereGeometry(1.4, 10, 8), tileSmall, p.x, hy + 15.6, p.z, 1, 0.7, 1);
      if (sd > 0) {
        const t = at(sd * 11 + 4.6, PV - 2), ty = height(t.x, t.z);
        add(new THREE.CylinderGeometry(1, 1.5, 13, 10), tileSmall, t.x, ty + 6.5, t.z);
        add(box, white, t.x, ty + 14.5, t.z, 0.6, 3, 0.6);
        add(box, white, t.x, ty + 15.4, t.z, 2.2, 0.6, 0.6);
      }
    });
    const gate = at(0, PV + 3.8); add(box, stone, gate.x, height(gate.x, gate.z) + 1.5, gate.z, 14, 3, 1, ry);
    // Сосны и деревья вокруг, мимо террасы, лестницы и домиков.
    for (let k = 0; k < 180; k++) {
      const rr = rand(16, 75), a = rnd() * Math.PI * 2, u = Math.cos(a) * rr, v = Math.sin(a) * rr;
      if ((u / (TU + 4)) ** 2 + (v / (TV + 4)) ** 2 < 1 || (Math.abs(u) < 17 && v > TV - 2 && v < PV + 7)) continue;
      const p = at(u, v);
      if (rnd() < 0.5) pine(p.x, p.z); else tree(p.x, p.z);
    }
  }

  yield 0.51;
  // ─── Площадь Каталонии, собор, колонна Колумба, Триумфальная арка ───────
  {
    // Площадь: светлое мощение, круг с фонтанами, газон.
    add(new THREE.CylinderGeometry(30, 30, 0.6, 32), lambert(0xd9d3c6), 0, 1.3, 0);
    add(new THREE.CylinderGeometry(16, 16, 0.4, 32), lambert(0xc4b9a3), 0, 1.7, 0);
    [[-14, 8], [14, -8]].forEach(([dx, dz]) => add(new THREE.CylinderGeometry(4, 4, 0.6, 16), new THREE.MeshPhongMaterial({ color: 0x6fb8d8, shininess: 80 }), dx, 1.8, dz));
    [[-16, -14], [16, 14], [-18, 12], [18, -12]].forEach(([dx, dz]) => add(box, lambert(0x6fae4f), dx, 1.5, dz, 12, 0.3, 9, Math.PI / 4));
    for (let k = 0; k < 14; k++) { const a = k / 14 * Math.PI * 2; tree(Math.cos(a) * 26, Math.sin(a) * 26); }

    // Собор: готика, тёмный камень, ажурные шпили.
    const { x, z } = P.cathedral, y0 = height(x, z);
    const gothic = lambert(0x9e9483);
    add(box, gothic, x, y0 + 12, z, 18, 24, 34, 0.5);
    add(new THREE.CylinderGeometry(1, 1, 1, 3), lambert(0x8a8070), x, y0 + 26, z, 9, 4, 33).rotation.set(Math.PI / 2, 0.5, Math.PI / 2);
    const spire = (dx, dz, h, r) => {
      add(new THREE.CylinderGeometry(r, r, h * 0.55, 8), gothic, x + dx, y0 + h * 0.275, z + dz);
      add(new THREE.ConeGeometry(r * 1.05, h * 0.45, 8), gothic, x + dx, y0 + h * 0.775, z + dz);
    };
    const front = { dx: Math.sin(0.5), dz: Math.cos(0.5) };
    spire(front.dx * 16, front.dz * 16, 58, 3.4);
    spire(front.dx * 14 + front.dz * 8, front.dz * 14 - front.dx * 8, 40, 2.2);
    spire(front.dx * 14 - front.dz * 8, front.dz * 14 + front.dx * 8, 40, 2.2);
    spire(-front.dx * 6 + front.dz * 9, -front.dz * 6 - front.dx * 9, 34, 2.4);

    // Колонна Колумба: постамент, высокая колонна, глобус и фигура.
    const c = P.columbus, cy = height(c.x, c.z);
    add(new THREE.CylinderGeometry(9, 10, 6, 8), lambert(0x8a7a64), c.x, cy + 3, c.z);
    add(new THREE.CylinderGeometry(4, 5, 8, 8), lambert(0xa89878), c.x, cy + 10, c.z);
    add(new THREE.CylinderGeometry(1.5, 1.9, 34, 12), lambert(0x6a5a44), c.x, cy + 31, c.z);
    add(new THREE.SphereGeometry(2.2, 10, 8), lambert(0x8a6a3a), c.x, cy + 49, c.z);
    add(new THREE.CylinderGeometry(0.8, 1, 4, 8), lambert(0x6a5a44), c.x, cy + 53, c.z);
    add(new THREE.SphereGeometry(0.7, 8, 6), lambert(0x6a5a44), c.x, cy + 55.6, c.z);
    // Рука Колумба указывает в море (на юго-восток).
    add(box, lambert(0x6a5a44), c.x - 1, cy + 54.5, c.z - 1, 0.4, 0.4, 3.4, Math.PI / 4);

    // Триумфальная арка: красный кирпич, бульвар с пальмами к Сьютаделье.
    const ar = P.arc, ay = height(ar.x, ar.z), brick = lambert(0xb5523a), brickL = lambert(0xc96a4a);
    const arcRy = Math.PI / 4;
    [-1, 1].forEach((sd) => add(box, brick, ar.x + Math.cos(arcRy) * sd * 6.5, ay + 12, ar.z - Math.sin(arcRy) * sd * 6.5, 5, 24, 7, arcRy));
    add(box, brick, ar.x, ay + 26, ar.z, 18, 6, 7, arcRy);
    add(new THREE.CylinderGeometry(4.2, 4.2, 7, 16, 1, false, 0, Math.PI), brickL, ar.x, ay + 20.5, ar.z).rotation.set(Math.PI / 2, 0, arcRy);
    add(box, lambert(0xd9b06a), ar.x, ay + 29.6, ar.z, 19, 1.2, 7.4, arcRy);
    const bv = fromAB(toAB(ar.x, ar.z).a, toAB(ar.x, ar.z).b - 1), dir = { x: bv.x - ar.x, z: bv.z - ar.z };
    for (let t = 10; t < 110; t += 9) [-1, 1].forEach((sd) => palm(ar.x + dir.x * t + dir.z * sd * 8, ar.z + dir.z * t - dir.x * sd * 8));
    road(ar.x, ar.z, ar.x + dir.x * 115, ar.z + dir.z * 115, 12, 0, 0);
  }

  yield 0.55;
  // ─── Сьютаделья: парк, озеро, Каскад с золотой квадригой ────────────────
  {
    const { x, z } = P.ciutadella, y0 = height(x, z);
    add(new THREE.CylinderGeometry(84, 84, 0.3, 36), lambert(0x6fa04a), x, y0 + 1.1, z);
    for (let k = 0; k < 240; k++) { const r = Math.sqrt(rnd()) * 80, a = rnd() * Math.PI * 2; const px = x + Math.cos(a) * r, pz = z + Math.sin(a) * r; if (Math.hypot(px - x - 20, pz - z + 10) > 22 && Math.hypot(px - x + 30, pz - z - 20) > 20) (rnd() < 0.2 ? palm : tree)(px, pz); }
    const lake = add(new THREE.CircleGeometry(18, 24), new THREE.MeshPhongMaterial({ color: 0x5f9fa8, shininess: 70 }), x + 20, y0 + 1.4, z - 10);
    lake.rotation.x = -Math.PI / 2; lake.scale.set(1.3, 0.7, 1);
    // Каскад: арка-грот, бассейн, золотая квадрига наверху.
    const cx = x - 30, cz = z + 20;
    add(box, lambert(0xd8c8a4), cx, y0 + 7, cz, 30, 14, 8);
    add(box, lambert(0xe2d4b4), cx, y0 + 15, cz, 14, 4, 6);
    add(box, new THREE.MeshLambertMaterial({ color: 0xd9a93a, emissive: 0x3a2800 }), cx, y0 + 19, cz, 6, 4, 3);
    add(new THREE.CylinderGeometry(12, 12, 0.6, 20, 1, false, 0, Math.PI), new THREE.MeshPhongMaterial({ color: 0x6fb8d8, shininess: 80 }), cx, y0 + 1.6, cz - 4);
  }

  yield 0.59;
  // ─── Торре Глориес: «пуля» в цветной мозаике, ночью светится ────────────
  const gloriesMat = (() => {
    const cv = document.createElement('canvas'); cv.width = 64; cv.height = 128;
    const g = cv.getContext('2d');
    for (let j = 0; j < 64; j++) for (let i = 0; i < 32; i++) {
      const t = j / 64;
      const pal = t < 0.3 ? ['#c8372d', '#e05a2a', '#a82a2a'] : t < 0.6 ? ['#e05a2a', '#3a6fb8', '#c8372d'] : ['#3a8fd0', '#5fb8e0', '#2a5fa8', '#bfe0f0'];
      g.fillStyle = pick(pal); g.fillRect(i * 2, 128 - j * 2 - 2, 2, 2);
    }
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace; tex.magFilter = THREE.NearestFilter;
    return new THREE.MeshLambertMaterial({ map: tex, emissiveMap: tex, emissive: 0xffffff, emissiveIntensity: 0 });
  })();
  {
    const { x, z } = P.glories, y0 = height(x, z), H = 92, R = 13;
    const prof = [];
    for (let i = 0; i <= 24; i++) { const t = i / 24; prof.push(new THREE.Vector2(R * Math.sqrt(Math.max(0, 1 - t ** 3.2)) + (t === 1 ? 0 : 0.01), t * H)); }
    add(new THREE.LatheGeometry(prof, 28), gloriesMat, x, y0, z);
    // Парк площади Глориес — к западу от башни (по карте), с поляной Гран Кларьяна.
    const gp = GLORIES_PARK, gy = height(gp.x, gp.z);
    add(new THREE.CylinderGeometry(42, 42, 0.3, 32), lambert(0x7fae5a), gp.x, gy + 1.2, gp.z);
    add(new THREE.CylinderGeometry(18, 18, 0.3, 24), lambert(0x9cc46a), gp.x + 6, gy + 1.35, gp.z - 4);
    for (let k = 0; k < 70; k++) { const rr = rand(20, 40), a = rnd() * Math.PI * 2; tree(gp.x + Math.cos(a) * rr, gp.z + Math.sin(a) * rr); }
  }

  yield 0.62;
  // ─── Олимпийский порт: отель Arts, Торре Мапфре, «Рыба» Гери, отель W ───
  {
    const { x, z } = P.arts, y0 = height(x, z);
    const n = { x: 1, z: -1 }; // вдоль берега на юго-запад
    const towerAt = (o) => ({ x: x + n.x * o * R2, z: z + n.z * o * R2 });
    // Arts: стеклянная башня в белом стальном каркасе.
    const t1 = towerAt(12);
    add(box, lambert(0x4a6070), t1.x, y0 + 45, t1.z, 15, 90, 15, Math.PI / 4);
    const frame = [];
    for (let yy = 4; yy < 92; yy += 8) frame.push({ x: t1.x, y: y0 + yy, z: t1.z, sx: 17, sy: 0.6, sz: 17, ry: Math.PI / 4 });
    [[-1, -1], [1, -1], [1, 1], [-1, 1]].forEach(([u, v]) => { const p = fromAB(toAB(t1.x, t1.z).a + u * 8.4 * R2 * 1.4, toAB(t1.x, t1.z).b + v * 8.4 * R2 * 1.4); frame.push({ x: p.x, y: y0 + 46, z: p.z, sx: 0.8, sy: 92, sz: 0.8 }); });
    instanced(box, lambert(0xf2f2ee), frame);
    // Мапфре: светлое стекло.
    const t2 = towerAt(-34);
    add(box, glassMats[1], t2.x, y0 + 44, t2.z, 15, 88, 15, Math.PI / 4);
    // «Рыба» Гери: золотая сетка.
    const f = towerAt(-8), fy = y0 + 14;
    const fish = add(new THREE.SphereGeometry(1, 16, 10), new THREE.MeshPhongMaterial({ color: 0xd9a93a, specular: 0xffe7a0, shininess: 60, wireframe: false }), f.x + 6, fy, f.z + 6, 22, 6, 7, -Math.PI / 4);
    fish.rotation.z = 0.15;
    add(new THREE.ConeGeometry(4, 8, 4), fish.material, f.x - 9, fy + 2, f.z - 9).rotation.set(0, Math.PI / 4, Math.PI / 2);
    // Марина Олимпийского порта: яхты.
    for (let k = 0; k < 18; k++) { const p = fromAB(toAB(x, z).a + rand(30, 90), coastB(toAB(x, z).a + 60) - rand(8, 30)); boat(p.x, p.z, 0.8); }

    // Отель W: «парус» на краю Барселонеты, зеркальное стекло.
    const w = P.whotel;
    const sail = new THREE.Shape(); sail.moveTo(-12, 0); sail.quadraticCurveTo(-4, 60, 0, 64); sail.quadraticCurveTo(4, 60, 12, 0); sail.closePath();
    const sg = new THREE.ExtrudeGeometry(sail, { depth: 10, bevelEnabled: false, curveSegments: 10 }); sg.translate(0, 0, -5);
    add(sg, new THREE.MeshPhongMaterial({ color: 0x8fb4c8, specular: 0xffffff, shininess: 120, flatShading: true }), w.x, height(w.x, w.z), w.z, 1, 1, 1, -Math.PI / 4);
    add(box, lambert(0xd8d2c2), w.x, 2, w.z, 50, 2, 30, Math.PI / 4);
  }
  function boat(x, z, k = 1) {
    const ry = rnd() * Math.PI;
    add(box, lambert(0xf4f4f0), x, 0.9, z, 2.4 * k, 1.2 * k, 7 * k, ry);
    const sl = add(new THREE.ConeGeometry(2.2 * k, 8 * k, 3), lambert(0xffffff), x, 5.4 * k, z, 0.35, 1, 1, ry);
    sl.rotation.y = ry;
  }

  yield 0.66;
  // ─── Порт Вель, Барселонета, пляжи ───────────────────────────────────────
  {
    // Яхты в бухте и парусники в море.
    for (let k = 0; k < 40; k++) {
      const p = { x: rand(-700, -300), z: rand(-500, -200) };
      if (inPort(p.x, p.z)) boat(p.x, p.z, 0.7);
    }
    for (let k = 0; k < 22; k++) {
      const a = rand(-900, 1300), p = fromAB(a, coastB(a) - rand(80, 500));
      if (Math.hypot(p.x, p.z) < ISLAND * 0.95) boat(p.x, p.z, 1.6);
    }
    // Пирсы-волнорезы у пляжей и зонтики.
    const umbrellas = [], poles = [];
    for (let a = toAB(P.whotel.x, P.whotel.z).a + 20; a < toAB(P.forum.x, P.forum.z).a; a += 6) {
      if (rnd() < 0.45) continue;
      const p = fromAB(a + rand(-2, 2), coastB(a) + rand(4, 16));
      poles.push({ x: p.x, y: 2.4, z: p.z, sx: 0.2, sy: 3, sz: 0.2 });
      umbrellas.push({ x: p.x, y: 4, z: p.z, sx: 1, sy: 1, sz: 1, ry: rnd() * 3 });
    }
    instanced(box, lambert(0xeeeeee), poles);
    instanced(new THREE.ConeGeometry(1.8, 0.9, 8), lambert(0xe85a3a), umbrellas.filter((_, i) => i % 3 === 0));
    instanced(new THREE.ConeGeometry(1.8, 0.9, 8), lambert(0x3a8fd0), umbrellas.filter((_, i) => i % 3 === 1));
    instanced(new THREE.ConeGeometry(1.8, 0.9, 8), lambert(0xf2d04a), umbrellas.filter((_, i) => i % 3 === 2));
    const piers = [];
    for (let a = toAB(P.arts.x, P.arts.z).a + 120; a < toAB(P.forum.x, P.forum.z).a; a += 110) {
      const p = fromAB(a, coastB(a) - 14);
      piers.push({ x: p.x, y: 1, z: p.z, sx: 5, sy: 1.6, sz: 34, ry: -Math.PI / 4 });
    }
    instanced(box, lambert(0x9c958a), piers);
    // Причалы Порта Вель и порт к югу от Монжуика: краны и контейнеры.
    const cont = [[], [], [], []];
    for (let k = 0; k < 700; k++) {
      const a = rand(toAB(P.miramar.x, P.miramar.z).a - 600, toAB(P.miramar.x, P.miramar.z).a - 120);
      const p = fromAB(a + rand(-4, 4), coastB(a) + rand(10, 60));
      if (rawHeight(p.x, p.z) > 4 || inLot(p.x, p.z, 10)) continue;
      cont[k % 4].push({ x: p.x, y: 2 + Math.floor(rnd() * 3) * 2.6, z: p.z, sx: 2.4, sy: 2.6, sz: 8, ry: -Math.PI / 4 });
    }
    [0xc8372d, 0x2d6fa8, 0xe8a83a, 0x3f8f5f].forEach((c, i) => instanced(box, lambert(c), cont[i]));
    for (let k = 0; k < 6; k++) {
      const a = toAB(P.miramar.x, P.miramar.z).a - 160 - k * 70, p = fromAB(a, coastB(a) + 4), q = fromAB(a, coastB(a) - 14);
      [-3, 3].forEach((o) => add(box, lambert(0x2d6fa8), p.x + o, 15, p.z + o, 1.4, 30, 1.4));
      add(box, lambert(0x2d6fa8), (p.x + q.x) / 2, 30, (p.z + q.z) / 2, 2, 2, 34, -Math.PI / 4);
    }
  }

  yield 0.70;
  // ─── Монжуик: MNAC, Волшебный фонтан, площадь Испании, крепость ─────────
  const fountainJets = [];
  {
    const m = P.mnac, my = levelAt(m);
    const palace = lambert(0xd9c9a4), palaceDark = lambert(0xbfae88);
    const ry = Math.atan2(P.espanya.x - m.x, P.espanya.z - m.z); // фасад смотрит на площадь Испании
    add(box, palace, m.x, my + 11, m.z, 70, 22, 44, ry);
    add(box, palaceDark, m.x, my + 22.6, m.z, 72, 1.4, 46, ry);
    add(new THREE.CylinderGeometry(12, 12, 8, 20), palace, m.x, my + 27, m.z);
    add(new THREE.SphereGeometry(12, 20, 10, 0, Math.PI * 2, 0, Math.PI / 2), lambert(0x9fa8b0), m.x, my + 31, m.z, 1, 1.3, 1);
    [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(([u, v]) => {
      const px = m.x + Math.cos(ry) * u * 32 + Math.sin(ry) * v * 18, pz = m.z - Math.sin(ry) * u * 32 + Math.cos(ry) * v * 18;
      add(new THREE.CylinderGeometry(3, 3, 32, 8), palace, px, my + 16, pz);
      add(new THREE.ConeGeometry(3.4, 6, 8), palaceDark, px, my + 35, pz);
    });
    // Каскад ступеней и фонтанов от дворца вниз к Волшебному фонтану.
    const f = P.fountain, fy = height(f.x, f.z);
    const steps = 9;
    for (let k = 0; k < steps; k++) {
      const t = (k + 0.5) / steps, px = m.x + (f.x - m.x) * (0.25 + t * 0.6), pz = m.z + (f.z - m.z) * (0.25 + t * 0.6);
      add(box, lambert(0xd8d0c0), px, height(px, pz) + 0.5, pz, 30, 1.2, 10, ry);
      if (k % 2) add(box, new THREE.MeshPhongMaterial({ color: 0x6fb8d8, shininess: 80 }), px, height(px, pz) + 1.2, pz, 6, 0.4, 9, ry);
    }
    // Волшебный фонтан: чаша и струи; ночью струи меняют цвет (updateAnim).
    add(new THREE.CylinderGeometry(22, 22, 1.2, 32), lambert(0xd8d0c0), f.x, fy + 0.8, f.z, 1, 1, 0.7);
    add(new THREE.CylinderGeometry(20, 20, 0.4, 32), new THREE.MeshPhongMaterial({ color: 0x6fb8d8, shininess: 80 }), f.x, fy + 1.5, f.z, 1, 1, 0.7);
    const jetMat = new THREE.MeshBasicMaterial({ color: 0xeaf6ff, transparent: true, opacity: 0.85 });
    for (let k = 0; k < 26; k++) {
      const a = k / 26 * Math.PI * 2, r = k % 2 ? 15 : 9;
      const j = add(new THREE.CylinderGeometry(0.4, 1.2, 1, 6), jetMat.clone(), f.x + Math.cos(a) * r, fy + 6, f.z + Math.sin(a) * r * 0.7, 1, 10, 1);
      fountainJets.push(j);
    }
    // У каждой струи свой материал: цвет меняется по струе, общий материал красил бы все одним.
    const center = add(new THREE.CylinderGeometry(0.8, 2.5, 1, 8), jetMat.clone(), f.x, fy + 12, f.z, 1, 22, 1); fountainJets.push(center);
    // Площадь Испании: круговая площадь с фонтаном, две Венецианские башни,
    // круглая арена «Лас Аренас».
    const e = P.espanya, ey = height(e.x, e.z), brick = lambert(0xb5603a), brickD = lambert(0x9a4a2a);
    add(new THREE.CylinderGeometry(30, 30, 0.6, 32), lambert(0xd9d3c6), e.x, ey + 1.2, e.z);
    add(new THREE.CylinderGeometry(6, 8, 6, 12), lambert(0xd9c9a4), e.x, ey + 4, e.z);
    const dx = (m.x - e.x), dz = (m.z - e.z), dl = Math.hypot(dx, dz), ux = dx / dl, uz = dz / dl;
    [-1, 1].forEach((sd) => {
      const tx = e.x + ux * 34 + uz * sd * 10, tz = e.z + uz * 34 - ux * sd * 10, ty = height(tx, tz);
      add(box, brick, tx, ty + 22, tz, 6, 44, 6);
      add(box, brickD, tx, ty + 45, tz, 7, 2, 7);
      add(box, brick, tx, ty + 48, tz, 5, 4, 5);
      add(new THREE.ConeGeometry(3.8, 8, 4), brickD, tx, ty + 54, tz).rotation.y = Math.PI / 4;
    });
    road(e.x + ux * 30, e.z + uz * 30, f.x - ux * 22, f.z - uz * 22, 14, 14, 0);
    const ar = { x: e.x - ux * 40 + uz * 30, z: e.z - uz * 40 - ux * 30 }, ay = height(ar.x, ar.z);
    add(new THREE.CylinderGeometry(24, 24, 16, 28), brick, ar.x, ay + 8, ar.z);
    add(new THREE.CylinderGeometry(24.5, 24.5, 1.2, 28), lambert(0xe8dcc0), ar.x, ay + 16, ar.z);
    add(new THREE.SphereGeometry(20, 20, 8, 0, Math.PI * 2, 0, Math.PI / 2), lambert(0xd8dde2), ar.x, ay + 16, ar.z, 1, 0.25, 1);

    // Крепость Монжуик: звёздный бастион на вершине.
    const c = P.castle, cy = levelAt(c);
    const star = new THREE.Shape();
    for (let i = 0; i < 8; i++) { const a = i / 8 * Math.PI * 2, r = i % 2 ? 18 : 28; i ? star.lineTo(Math.cos(a) * r, Math.sin(a) * r) : star.moveTo(Math.cos(a) * r, Math.sin(a) * r); }
    const sgeo = new THREE.ExtrudeGeometry(star, { depth: 8, bevelEnabled: false }); sgeo.rotateX(-Math.PI / 2);
    add(sgeo, lambert(0xb8a882), c.x, cy - 2, c.z);
    add(box, lambert(0xc9b996), c.x, cy + 9, c.z, 20, 6, 20);
    add(box, lambert(0xd8452f), c.x, cy + 16, c.z, 0.3, 8, 0.3);
    add(box, lambert(0xf2d04a), c.x + 1.6, cy + 19, c.z, 3, 2, 0.1);
    // Олимпийский стадион и башня Калатравы.
    const st = P.stadium, sy = levelAt(st);
    const bowl = add(new THREE.CylinderGeometry(30, 26, 12, 32, 1, true), new THREE.MeshLambertMaterial({ color: 0xe0d6c0, side: THREE.DoubleSide }), st.x, sy + 6, st.z, 1, 1, 0.75);
    add(new THREE.CylinderGeometry(24, 24, 0.4, 28), lambert(0x4f9a4a), st.x, sy + 0.8, st.z, 1, 1, 0.7);
    const ct = P.calatrava, cty = height(ct.x, ct.z);
    const mast = add(box, lambert(0xf4f4f0), ct.x, cty + 30, ct.z, 2, 64, 3);
    mast.rotation.z = 0.32;
    add(new THREE.TorusGeometry(9, 0.9, 6, 24, Math.PI * 0.9), lambert(0xf4f4f0), ct.x - 4, cty + 16, ct.z).rotation.set(0, Math.PI / 2, 0.5);
    add(new THREE.CylinderGeometry(0.4, 0.4, 14, 6), lambert(0xf4f4f0), ct.x - 12, cty + 52, ct.z).rotation.z = 0.32;
    // Сосны по склонам Монжуика.
    for (let k = 0; k < 600; k++) {
      const x = P.castle.x + rand(-300, 300), z = P.castle.z + rand(-220, 240), h = rawHeight(x, z);
      if (h < 14 || inLot(x, z, 4)) continue;
      pine(x, z);
    }
  }

  yield 0.74;
  // ─── Канатка через порт: Сант-Себастья → Жауме I → Мирамар ──────────────
  const cable: { cabins: any[]; path: THREE.Vector3[] } = { cabins: [], path: [] };
  {
    const steel = lambert(0x9aa0a6), red = lambert(0xc8372d);
    const ss = P.stSebastia, jm = P.jaume, mr = P.miramar;
    const latt = (p, h) => {
      add(box, steel, p.x, height(p.x, p.z) + h / 2, p.z, 3, h, 3);
      add(box, steel, p.x, height(p.x, p.z) + h, p.z, 9, 4, 9);
      add(box, red, p.x, height(p.x, p.z) + h + 3, p.z, 6, 2, 6);
    };
    latt(ss, 50); latt(jm, 66);
    const my = height(mr.x, mr.z);
    add(box, lambert(0xe2d6bc), mr.x, my + 4, mr.z, 14, 8, 10);
    cable.path = [new THREE.Vector3(ss.x, height(ss.x, ss.z) + 52, ss.z), new THREE.Vector3(jm.x, height(jm.x, jm.z) + 68, jm.z), new THREE.Vector3(mr.x, my + 7, mr.z)];
    const pts = [];
    for (let i = 0; i < cable.path.length - 1; i++) pts.push(cable.path[i], cable.path[i + 1]);
    scene.add(new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(pts), new THREE.LineBasicMaterial({ color: 0x333333 })));
    const cabinMat = lambert(0xd8352a);
    for (let k = 0; k < 6; k++) cable.cabins.push({ mesh: add(box, cabinMat, 0, 0, 0, 2.6, 2.6, 2.6), t: k / 6 });
  }
  const cablePoint = (t) => {
    const n = cable.path.length - 1, f = t * n, i = Math.min(n - 1, Math.floor(f));
    return cable.path[i].clone().lerp(cable.path[i + 1], f - i);
  };

  yield 0.78;
  // ─── Камп Ноу ────────────────────────────────────────────────────────────
  // По фото: низкая широкая чаша формы скруглённого прямоугольника (суперэллипс),
  // три яруса — нижний синий, средний и верхний бордовые; на нижнем ярусе
  // дальней длинной трибуны жёлтая надпись «MÉS QUE / UN CLUB». Белый навес
  // только над главной трибуной, снаружи — бетон с горизонтальными поясами,
  // поле в полосах.
  {
    const { x, z } = P.campnou, y0 = height(x, z);
    // Всё, что добавится ниже, потом увеличим вокруг центра в STADIUM_K раз: рядом с
    // домами (они выше реального масштаба) стадион в честных размерах выглядел мелким.
    const childrenBefore = scene.children.length;
    const ry = Math.PI / 4;
    const A = 20, B = 13.5, N = 4;       // кромка у поля: полудлина, полуширина, «квадратность»
    const sp = (t, o) => {               // точка суперэллипса, раздутого на o
      const c = Math.cos(t), sn = Math.sin(t);
      return [(A + o) * Math.sign(c) * Math.abs(c) ** (2 / N), (B + o) * Math.sign(sn) * Math.abs(sn) ** (2 / N)];
    };
    // Профиль трибун: [смещение от кромки, высота, ярус]; ярус 0 — синий.
    const prof = [[0, 1.8, 0], [7, 7.5, 0], [8, 7.5, -1], [14, 13, 1], [15, 13, -1], [21.5, 20.5, 2], [22, 21, -1]];
    const blue = new THREE.Color(0x1f4f9a), blue2 = new THREE.Color(0x2a5fae);
    const claret = new THREE.Color(0x8e1f3f), claret2 = new THREE.Color(0xa52a4a), walk = new THREE.Color(0xbdb8ae);
    const SEG = 220, pos = [], col = [];
    const tile = (p1, p2, p3, p4, c) => { [p1, p2, p3, p1, p3, p4].forEach((p) => { pos.push(p[0], p[1], p[2]); col.push(c.r, c.g, c.b); }); };
    for (let i = 0; i < SEG; i++) {
      const t1 = i / SEG * Math.PI * 2, t2 = (i + 1) / SEG * Math.PI * 2;
      for (let k = 0; k < prof.length - 1; k++) {
        const [o1, h1] = prof[k], [o2, h2, tier] = prof[k + 1];
        // Ряды внутри яруса — полосками, чтобы читались ступени.
        const rows = tier < 0 ? 1 : 4;
        for (let r = 0; r < rows; r++) {
          const fa = r / rows, fb = (r + 1) / rows;
          const oa = o1 + (o2 - o1) * fa, ob = o1 + (o2 - o1) * fb, ha = h1 + (h2 - h1) * fa, hb = h1 + (h2 - h1) * fb;
          const a1 = sp(t1, oa), a2 = sp(t2, oa), b1 = sp(t1, ob), b2 = sp(t2, ob);
          let c = tier < 0 ? walk : tier === 0 ? (r % 2 ? blue : blue2) : (r % 2 ? claret : claret2);
          tile([a1[0], ha, a1[1]], [b1[0], hb, b1[1]], [b2[0], hb, b2[1]], [a2[0], ha, a2[1]], c);
        }
      }
      // Внешняя стена: бетон с тёмными поясами.
      const w1 = sp(t1, 22), w2 = sp(t2, 22);
      [[0, 6, 0xd9d1c2], [6, 7, 0x8f8a80], [7, 13, 0xd9d1c2], [13, 14, 0x8f8a80], [14, 21, 0xe2dbcd]].forEach(([ya, yb, cc]) => {
        tile([w1[0], ya, w1[1]], [w2[0], ya, w2[1]], [w2[0], yb, w2[1]], [w1[0], yb, w1[1]], new THREE.Color(cc));
      });
    }
    const bowlGeo = new THREE.BufferGeometry();
    bowlGeo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    bowlGeo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    bowlGeo.computeVertexNormals();
    add(bowlGeo, new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide, flatShading: true }), x, y0, z, 1, 1, 1, ry);
    // Вертикальные рёбра-лестницы по фасаду.
    const ribs = [];
    for (let i = 0; i < 40; i++) {
      const [px, pz] = sp(i / 40 * Math.PI * 2, 22.4), cx = x + px * Math.cos(ry) + pz * Math.sin(ry), cz = z - px * Math.sin(ry) + pz * Math.cos(ry);
      ribs.push({ x: cx, y: y0 + 10.5, z: cz, sx: 1.2, sy: 21, sz: 1.2, ry });
    }
    instanced(box, lambert(0xc4bcae), ribs);
    // Поле в полосах.
    const local = (u, v) => ({ x: x + u * Math.cos(ry) + v * Math.sin(ry), z: z - u * Math.sin(ry) + v * Math.cos(ry) });
    for (let k = 0; k < 10; k++) { const p = local(-17.1 + k * 3.8, 0); add(box, lambert(k % 2 ? 0x3f9a3f : 0x4aa84a), p.x, y0 + 1.5, p.z, 3.8, 0.3, 24, ry); }
    const lineMat = lambert(0xffffff);
    [[0, 0, 0.25, 21], [-16, 0, 0.25, 21], [16, 0, 0.25, 21], [0, 10.5, 32, 0.25], [0, -10.5, 32, 0.25], [-14, 0, 0.25, 8], [14, 0, 0.25, 8]]
      .forEach(([u, v, w, d]) => { const p = local(u, v); add(box, lineMat, p.x, y0 + 1.7, p.z, w, 0.1, d, ry); });
    const cc = local(0, 0); add(new THREE.RingGeometry(2.8, 3.1, 24), lineMat, cc.x, y0 + 1.72, cc.z).rotation.x = -Math.PI / 2;
    // Белый навес над главной трибуной (ближняя длинная сторона, z > 0).
    const roof = local(0, B + 16);
    const r1 = add(box, lambert(0xf2f2ee), roof.x, y0 + 23.5, roof.z, 2 * A + 6, 0.6, 11, ry);
    r1.rotation.set(-0.12, ry, 0, 'YXZ');
    const stripes = [];
    for (let u = -A - 2; u <= A + 2; u += 2.4) { const p = local(u, B + 16); stripes.push({ x: p.x, y: y0 + 24, z: p.z, sx: 0.4, sy: 0.4, sz: 11, ry }); }
    instanced(box, lambert(0xb8bcc2), stripes);
    // Стеклянный фасад главной трибуны.
    const gl = local(0, B + 22.6); add(box, lightMat(0x6f8fa8, 0xffe0a0), gl.x, y0 + 6, gl.z, 2 * A, 9, 1, ry);
    // Надпись «MÉS QUE / UN CLUB» — текстура на плоскости, уложенной на наклон нижнего
    // яруса дальней трибуны (раскраска рядов сидений давала рваные буквы).
    const cv = document.createElement('canvas'); cv.width = 1024; cv.height = 256;
    const g = cv.getContext('2d');
    g.fillStyle = '#f2c230'; g.font = 'bold 116px sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText('MÉS QUE', 512, 66); g.fillText('UN CLUB', 512, 192);
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4;
    const slope = Math.atan2(7.5 - 1.8, 7);                 // наклон нижнего яруса (вынос 7, подъём 5,7)
    const lp = local(0, -(B + 3.5));
    const label = add(new THREE.PlaneGeometry(2 * A * 0.82, Math.hypot(7, 5.7) * 0.8), new THREE.MeshBasicMaterial({ map: tex, transparent: true, alphaTest: 0.3 }), lp.x, y0 + 4.65 + 0.15, lp.z);
    label.rotation.set(-Math.PI / 2 + slope, ry, 0, 'YXZ');
    // Увеличение стадиона вокруг центра.
    const STADIUM_K = 1.45;
    const stadium = new THREE.Group();
    stadium.position.set(x, y0, z); stadium.scale.setScalar(STADIUM_K);
    scene.children.slice(childrenBefore).forEach((o) => { o.position.sub(stadium.position); stadium.add(o); });
    scene.add(stadium);
  }

  yield 0.82;
  // ─── Тибидабо: храм Святого Сердца, колесо обозрения, башня Кольсерола ──
  const wheel = { mesh: null as THREE.Object3D | null };
  {
    const { x, z } = P.tibidabo, y0 = height(x, z);
    // Храм Святого Сердца, как на фото: внизу — крипта из бурого камня с
    // круглыми угловыми башнями и аркой входа с мозаикой, на ней терраса;
    // выше — белая неоготическая церковь: фасад с розой и тремя порталами,
    // башни-пинакли по углам, центральная башня с восьмигранным шпилем и
    // бронзовая статуя Христа с раскинутыми руками. Фасад смотрит на город.
    const ry = Math.atan2(-x, -z);
    const local = (u, v) => ({ x: x + u * Math.cos(ry) + v * Math.sin(ry), z: z - u * Math.sin(ry) + v * Math.cos(ry) });
    const at = (u, v, yy, geom, mat, sx = 1, sy = 1, sz = 1, rot = 0) => { const p = local(u, v); return add(geom, mat, p.x, y0 + yy, p.z, sx, sy, sz, ry + rot); };
    // Белый камень с подсветкой: на склоне в тени ламберт давал серый.
    const crypt = lambert(0xc9ab80, { emissive: 0x4a3a26 }), cryptDark = lambert(0x9c7f5c), white = lambert(0xf4f1ea, { emissive: 0x8c8a84 }), whiteShade = lambert(0xe2ddd2, { emissive: 0x76736c });
    const shadow = lambert(0x4a4a50), paving = lambert(0xd8d0c0);
    const bronze = new THREE.MeshLambertMaterial({ color: 0x8a7650, emissive: 0x1a1408 });
    const oct = new THREE.CylinderGeometry(1, 1, 1, 8), octCone = new THREE.ConeGeometry(1, 1, 8);
    // Крипта и терраса.
    at(0, 0, 7, box, crypt, 44, 14, 50);
    at(0, 0, 14.3, box, paving, 44.6, 0.6, 50.6);
    const rail = [];
    for (let u = -21; u <= 21; u += 2) rail.push({ ...local(u, 25), y: y0 + 15.3, sx: 0.8, sy: 1.4, sz: 0.8 });
    instanced(box, cryptDark, rail.map((r) => ({ x: r.x, y: r.y, z: r.z, sx: r.sx, sy: r.sy, sz: r.sz, ry })));
    [-1, 1].forEach((sd) => {
      at(sd * 20, 24, 10, new THREE.CylinderGeometry(3.2, 3.4, 20, 12), crypt);
      at(sd * 20, 24, 20.5, new THREE.CylinderGeometry(3.6, 3.6, 1, 12), cryptDark);
      at(sd * 20, 24, 22.5, new THREE.SphereGeometry(2.4, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), cryptDark, 1, 1.4, 1);
    });
    // Вход в крипту: арка-портал с мозаикой.
    at(0, 25.2, 6, box, cryptDark, 14, 12, 1);
    at(0, 25.8, 5, new THREE.CircleGeometry(4.5, 16, 0, Math.PI), shadow);
    at(0, 25.8, 2.5, box, shadow, 9, 5, 0.6);
    at(0, 25.8, 10, box, lambert(0xc8a040), 8, 2.4, 0.4);
    // Белая церковь: неф и трансепт.
    const ch = 14.6;
    at(0, -4, ch + 13, box, white, 24, 26, 30);
    at(0, -6, ch + 11, box, whiteShade, 34, 22, 12);
    at(0, -4, ch + 27, new THREE.ConeGeometry(17, 6, 4), whiteShade, 1, 1, 1.1, Math.PI / 4);
    // Фасад: ступенчатый щипец, роза, три портала.
    at(0, 11.5, ch + 15, box, white, 22, 30, 3);
    at(0, 13.05, ch + 20, new THREE.CircleGeometry(4, 20), shadow);
    at(0, 13.1, ch + 20, new THREE.CircleGeometry(3.2, 20), lightMat(0x5a7aa0, 0xffc870));
    [-6, 0, 6].forEach((u) => {
      at(u, 13.2, ch + 4, box, shadow, u ? 3 : 4, u ? 7 : 8, 0.4);
      at(u, 13.2, ch + (u ? 8 : 9), octCone, shadow, u ? 1.5 : 2, 3, 0.2);
    });
    // Пинакли по краям щипца и крыши.
    const pins = [];
    const pin = (u, v, yy, h) => { const p = local(u, v); pins.push({ x: p.x, y: y0 + yy + h / 2, z: p.z, sx: 0.9, sy: h, sz: 0.9 }); };
    [-10, -6, -2, 2, 6, 10].forEach((u) => pin(u, 12.5, ch + 30 - Math.abs(u) * 0.4, 6));
    for (let v = -16; v <= 8; v += 4) [-12.5, 12.5].forEach((u) => pin(u, v, ch + 26, 4));
    [-17, 17].forEach((u) => [-11, -1].forEach((v) => pin(u, v, ch + 22, 5)));
    instanced(octCone, white, pins);
    // Угловые башни фасада с острыми шпилями.
    [-1, 1].forEach((sd) => {
      at(sd * 11, 11, ch + 20, box, white, 5, 40, 5);
      at(sd * 11, 11, ch + 41, oct, whiteShade, 3, 3, 3);
      at(sd * 11, 11, ch + 48, octCone, white, 3, 12, 3);
      [-1, 1].forEach((k) => at(sd * 11 + k * 2.6, 11 + 2.6, ch + 42, octCone, white, 0.8, 5, 0.8));
    });
    // Центральная башня: квадратный ярус, восьмигранный барабан, шпиль.
    at(0, -2, ch + 34, box, white, 11, 16, 11);
    [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(([u, v]) => at(u * 5.6, -2 + v * 5.6, ch + 46, octCone, white, 1.2, 8, 1.2));
    at(0, -2, ch + 48, oct, white, 4.6, 12, 4.6);
    for (let k = 0; k < 8; k++) { const a = k / 8 * Math.PI * 2; at(Math.cos(a) * 4.6, -2 + Math.sin(a) * 4.6, ch + 57, octCone, white, 0.7, 5, 0.7); }
    at(0, -2, ch + 60, octCone, whiteShade, 3.4, 12, 3.4);
    // Статуя Христа с раскинутыми руками.
    const sy = ch + 66;
    at(0, -2, sy, oct, bronze, 0.9, 2, 0.9);
    at(0, -2, sy + 3.6, new THREE.CylinderGeometry(0.9, 1.5, 5.6, 8), bronze);
    at(0, -2, sy + 6.2, box, bronze, 9, 0.9, 0.9);
    [-1, 1].forEach((sd) => at(sd * 4.6, -2, sy + 6.6, box, bronze, 1, 0.8, 1, 0));
    at(0, -2, sy + 7.6, new THREE.SphereGeometry(0.85, 8, 6), bronze);
    // Парк аттракционов: красное колесо обозрения.
    const wx = x + 34, wz = z - 18, wy = height(wx, wz);
    const w = new THREE.Group(); w.position.set(wx, wy + 16, wz); w.rotation.y = Math.PI / 4; scene.add(w);
    w.add(new THREE.Mesh(new THREE.TorusGeometry(12, 0.6, 6, 32), lightMat(0x8a2a22, 0xff3030)));
    for (let k = 0; k < 10; k++) {
      const a = k / 10 * Math.PI * 2;
      const sp = new THREE.Mesh(box, lightMat(0xb08a2a, 0xffd040)); sp.scale.set(0.3, 24, 0.3); sp.rotation.z = a; w.add(sp);
      const cab = new THREE.Mesh(box, lambert(k % 2 ? 0xf2c230 : 0x2d6fa8)); cab.position.set(Math.cos(a) * 12, Math.sin(a) * 12, 0); cab.scale.set(2.2, 2.2, 2.2); w.add(cab);
    }
    wheel.mesh = w;
    [-1, 1].forEach((k) => { const leg = add(box, lambert(0xdddddd), wx, wy + 8, wz, 0.8, 17, 0.8); leg.rotation.set(0, Math.PI / 4, k * 0.35); });
    // Башня Кольсерола (Фостер): игла на растяжках, «диски» площадок.
    const c = P.collserola, cy = height(c.x, c.z), H = 150;
    add(new THREE.CylinderGeometry(1.6, 2.4, H, 10), lambert(0xd8dde2), c.x, cy + H / 2, c.z);
    add(new THREE.CylinderGeometry(10, 10, 24, 3), lambert(0xa8aeb4), c.x, cy + 62, c.z);
    for (let k = 0; k < 7; k++) add(new THREE.CylinderGeometry(10.6, 10.6, 0.6, 3), lambert(0xe8ecef), c.x, cy + 50 + k * 4, c.z);
    add(new THREE.CylinderGeometry(0.3, 0.8, 40, 6), lambert(0xd84a3a), c.x, cy + H + 20, c.z);
    const tl = add(new THREE.SphereGeometry(1, 8, 6), lightMat(0x8a2a22, 0xff3b30), c.x, cy + H + 41, c.z);
    redLights.push(tl);
    const guys = [];
    for (let k = 0; k < 3; k++) {
      const a = k / 3 * Math.PI * 2, gx = c.x + Math.cos(a) * 60, gz = c.z + Math.sin(a) * 60;
      guys.push(new THREE.Vector3(c.x, cy + 66, c.z), new THREE.Vector3(gx, height(gx, gz), gz));
      guys.push(new THREE.Vector3(c.x, cy + 110, c.z), new THREE.Vector3(gx, height(gx, gz), gz));
    }
    scene.add(new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(guys), new THREE.LineBasicMaterial({ color: 0x555555 })));
    // Лес Кольсеролы: пинии и кусты по хребту.
    for (let k = 0; k < 3600; k++) {
      const px = rand(-ISLAND, ISLAND), pz = rand(-ISLAND, ISLAND), h = rawHeight(px, pz);
      if (h < 30 || Math.hypot(px, pz) > ISLAND || inLot(px, pz, 4)) continue;
      pine(px, pz);
    }
  }

  yield 0.88;
  blocks.forEach((list, mat) => instanced(ringGeo, mat, list));
  instanced(ringCourt, lambert(0xc4b9a8), courts);
  bodies.forEach((list, mat) => instanced(box, mat, list));
  const DETAIL_SECTOR = 400, DETAIL_RANGE = 800;
  const detail = distanceCuller(DETAIL_RANGE);
  const nearOnly = (geom, material, list) => { if (list.length) detail.add(instancedBySector(scene, geom, material, list, DETAIL_SECTOR)); };
  // Деревья внутри домов и кварталов (не во дворе) и на полотне главных улиц убираем;
  // ствол и крона — парами, у пальмы шесть листьев подряд.
  // Пятна раскладываем по сетке 40 × 40: дерево смотрит только свою и соседние ячейки
  // (перебор всех ~10 тыс. пятен на каждое дерево стоил ~170 мс на Mac — секунды на ТВ).
  const FP_CELL = 40, fpGrid = new Map();
  FOOTPRINTS.forEach((f) => {
    const key = `${Math.floor(f.x / FP_CELL)}:${Math.floor(f.z / FP_CELL)}`;
    if (!fpGrid.has(key)) fpGrid.set(key, []);
    fpGrid.get(key).push(f);
  });
  const treeBlocked = (it) => {
    const cx = Math.floor(it.x / FP_CELL), cz = Math.floor(it.z / FP_CELL);
    for (let i = -1; i <= 1; i++) for (let j = -1; j <= 1; j++) {
      const list = fpGrid.get(`${cx + i}:${cz + j}`);
      if (list && list.some((f) => { const d = Math.hypot(it.x - f.x, it.z - f.z); return d < f.r && d > f.hole; })) return true;
    }
    return onMainRoad(it.x, it.z, 0.5);
  };
  for (let i = trunks.length - 1; i >= 0; i--) if (treeBlocked(trunks[i])) { trunks.splice(i, 1); crowns.splice(i, 1); }
  for (let i = palmTrunks.length - 1; i >= 0; i--) if (treeBlocked(palmTrunks[i])) { palmTrunks.splice(i, 1); palmLeaves.splice(i * 6, 6); }
  for (let i = lampPoles.length - 1; i >= 0; i--) if (treeBlocked(lampPoles[i])) { lampPoles.splice(i, 1); lampHeads.splice(i, 1); }
  nearOnly(box, lambert(0x8a6a4a), trunks);
  nearOnly(new THREE.IcosahedronGeometry(1, 0), lambert(0x5f8f3e), crowns);
  nearOnly(box, lambert(0x9a7a52), palmTrunks);
  nearOnly(box, lambert(0x4f8f3a), palmLeaves);
  nearOnly(box, lambert(0x3a3c40), lampPoles);
  nearOnly(box, lightMat(0x8a8474, 0xffd98a), lampHeads);

  yield 0.91;
  // ─── Звёзды для сбора ────────────────────────────────────────────────────
  const starShape = new THREE.Shape();
  for (let i = 0; i < 10; i++) {
    const a = i / 10 * Math.PI * 2 - Math.PI / 2, r = i % 2 ? 0.45 : 1;
    i ? starShape.lineTo(Math.cos(a) * r, Math.sin(a) * r) : starShape.moveTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  const starGeo = new THREE.ExtrudeGeometry(starShape, { depth: 0.35, bevelEnabled: false }); starGeo.center();
  const starMat = new THREE.MeshLambertMaterial({ color: 0xffd23f, emissive: 0x8a5a00 });
  const pickups = [];
  function star(x, y, z) { const s = add(starGeo, starMat, x, y, z, 2.4, 2.4, 2.4); pickups.push(s); }
  {
    // Спираль вокруг центральной башни Саграды Фамилии.
    const sg = P.sagrada, sy = height(sg.x, sg.z);
    for (let i = 0; i < 14; i++) { const a = i * 0.9; star(sg.x + Math.cos(a) * 34, sy + 20 + i * 8, sg.z + Math.sin(a) * 34); }
    // Вдоль канатки над портом.
    for (let t = 0.08; t < 1; t += 0.12) { const p = cablePoint(t); star(p.x, p.y + 10, p.z); }
    // Кольцо над Волшебным фонтаном, над Камп Ноу, вокруг колеса Тибидабо.
    const f = P.fountain, fy = height(f.x, f.z);
    for (let i = 0; i < 8; i++) { const a = i / 8 * Math.PI * 2; star(f.x + Math.cos(a) * 30, fy + 26, f.z + Math.sin(a) * 30); }
    const cn = P.campnou, cny = height(cn.x, cn.z);
    for (let i = 0; i < 10; i++) { const a = i / 10 * Math.PI * 2; star(cn.x + Math.cos(a) * 70, cny + 40, cn.z + Math.sin(a) * 60); }
    const tb = P.tibidabo, tby = height(tb.x, tb.z);
    for (let i = 0; i < 8; i++) { const a = i / 8 * Math.PI * 2; star(tb.x + Math.cos(a) * 40, tby + 80, tb.z + Math.sin(a) * 40); }
    // Вдоль Глориес снизу вверх и над пляжем.
    for (let y = 20; y < 100; y += 16) star(P.glories.x + 18, height(P.glories.x, P.glories.z) + y, P.glories.z);
    for (let a = toAB(P.whotel.x, P.whotel.z).a + 60; a < toAB(P.forum.x, P.forum.z).a; a += 90) { const p = fromAB(a, coastB(a) - 6); star(p.x, 18, p.z); }
    [['milà', 34], ['batllo', 32], ['cathedral', 70], ['columbus', 66], ['arc', 40], ['guell', 30], ['arts', 104], ['whotel', 76], ['mnac', 60], ['castle', 30], ['espanya', 64]].forEach(([k, y]: any) => star(P[k].x, height(P[k].x, P[k].z) + y, P[k].z));
    star(0, 30, 0);
    // Дорожки звёзд между достопримечательностями.
    [['sagrada', 'glories'], ['sagrada', 'guell'], ['batllo', 'sagrada'], ['columbus', 'mnac'], ['arts', 'ciutadella'], ['guell', 'tibidabo'], ['espanya', 'campnou']].forEach(([a, b]) => {
      const A = P[a], B = P[b];
      for (let t = 0.15; t < 0.9; t += 0.15) {
        const px = A.x + (B.x - A.x) * t, pz = A.z + (B.z - A.z) * t;
        star(px, height(px, pz) + 35 + Math.sin(t * Math.PI) * 20, pz);
      }
    });
  }

  yield 0.95;
  // ─── Время суток ─────────────────────────────────────────────────────────
  const K = [
    { t: 0.0, top: 0x3a86d0, mid: 0x9ccbec, horizon: 0xe2eef8, fog: 0xc9def0, sun: 0xfff2dc, sunI: 1.75, hemi: 1.5, sunAlt: 0.6 },
    { t: 0.5, top: 0x2e3a6e, mid: 0xc0708c, horizon: 0xffa95a, fog: 0xe0a07a, sun: 0xffa05a, sunI: 1.5, hemi: 1.1, sunAlt: 0.04 },
    { t: 1.0, top: 0x05081a, mid: 0x0f1838, horizon: 0x2a2748, fog: 0x161b33, sun: 0x8fa6ff, sunI: 0.55, hemi: 0.8, sunAlt: -0.25 },
  ];
  const tmpA = new THREE.Color(), tmpB = new THREE.Color();
  function lerpHex(a, b, k) { return tmpA.setHex(a).lerp(tmpB.setHex(b), k).clone(); }
  let glowNow = 0;
  function setTime(t) {
    const i = t < 0.5 ? 0 : 1, a = K[i], b = K[i + 1], k = (t - a.t) / (b.t - a.t);
    skyUniforms.top.value.copy(lerpHex(a.top, b.top, k));
    skyUniforms.mid.value.copy(lerpHex(a.mid, b.mid, k));
    skyUniforms.horizon.value.copy(lerpHex(a.horizon, b.horizon, k));
    fog.color.copy(lerpHex(a.fog, b.fog, k));
    const sunColor = lerpHex(a.sun, b.sun, k);
    const alt = THREE.MathUtils.lerp(a.sunAlt, b.sunAlt, k);
    // Солнце садится за Кольсеролу (северо-запад), днём — над морем на юге.
    const sunDir = new THREE.Vector3(0.6, Math.max(alt, -0.2), 0.2 - 1.2 * smooth(0, 0.5, t) + 0.0).normalize();
    sun.position.copy(sunDir).multiplyScalar(1000);
    sun.color.copy(sunColor); sun.intensity = THREE.MathUtils.lerp(a.sunI, b.sunI, k);
    hemi.intensity = THREE.MathUtils.lerp(a.hemi, b.hemi, k);
    hemi.color.copy(lerpHex(0xffffff, 0x9fb0ff, smooth(0.5, 1, t)));
    skyUniforms.sunDir.value.copy(sunDir);
    skyUniforms.sunColor.value.copy(sunColor);
    skyUniforms.sunStrength.value = 1 - smooth(0.62, 0.8, t);
    const glow = smooth(0.35, 0.75, t);
    glowNow = glow;
    lights.forEach((l) => l.m.color.copy(l.day).lerp(l.night, glow));
    facadeGlow.value = glow;
    gloriesMat.emissiveIntensity = glow * 0.9;
    nightStars.material.opacity = smooth(0.65, 0.95, t);
    fog.near = THREE.MathUtils.lerp(FOG_NEAR_DAY, FOG_NEAR_NIGHT, smooth(0.5, 1, t));
  }
  const jetColor = new THREE.Color();
  function updateAnim(dt, elapsed) {
    cable.cabins.forEach((c) => {
      c.t = (c.t + dt * 0.02) % 1;
      const p = cablePoint(c.t);
      c.mesh.position.set(p.x, p.y - 2.4, p.z);
    });
    if (wheel.mesh) wheel.mesh.rotation.z = elapsed * 0.15;
    // Волшебный фонтан: струи «дышат», ночью переливаются цветами.
    fountainJets.forEach((j, i) => {
      const s = 0.7 + 0.3 * Math.sin(elapsed * 1.8 + i * 0.6);
      j.scale.y = (i === fountainJets.length - 1 ? 22 : 10) * s;
      jetColor.setHSL((elapsed * 0.05 + i / fountainJets.length) % 1, 0.9, 0.6);
      j.material.color.setRGB(1, 1, 1).lerp(jetColor, glowNow);
    });
  }

  yield 0.97;
  const animated = [sky, nightStars, ...pickups, ...redLights, ...cable.cabins.map((c) => c.mesh), ...fountainJets];
  if (wheel.mesh) animated.push(wheel.mesh);
  mergeStaticMeshes(scene, animated, lights.map((l) => l.m), SECTOR_SIZE);

  setTime(0);
  updateAnim(0, 0);
  // Старт над морем у отеля Arts, курс на северо-запад — на Саграду Фамилию
  // и Тибидабо за ней.
  const spawnAt = fromAB(toAB(P.arts.x, P.arts.z).a - 110, coastB(toAB(P.arts.x, P.arts.z).a - 110) - 170);
  const spawnYaw = Math.atan2(-(P.sagrada.x - spawnAt.x), -(P.sagrada.z - spawnAt.z));
  return {
    height,
    pickups,
    spawn: { x: spawnAt.x, y: 60, z: spawnAt.z, yaw: spawnYaw },
    radius: ISLAND * 0.92,
    // Потолок выше Тибидабо с храмом (~350 м).
    maxY: 420,
    update(dt, elapsed, timeOfDay, cameraPosition) {
      setTime(timeOfDay);
      pickups.forEach((s) => { s.rotation.y = elapsed * 2; });
      updateAnim(dt, elapsed);
      redLights.forEach((l) => { l.visible = timeOfDay < 0.35 || Math.sin(elapsed * 3) > -0.3; });
      detail.update(cameraPosition);
      sky.position.copy(cameraPosition);
      nightStars.position.copy(cameraPosition);
    },
  };
}
